import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import Axios from "helpers/Interceptor";
import {
    CampaignDetails,
    TargetingAudienceGroup,
    InstagramPost,
    TargetedLanguage,
    PlaybookStep,
    TikTokSparkAd,
    TikTokTargetingGeography,
    SearchPlatformInterestsResult,
    ExternalLink,
    RecordDetails,
    MediaType,
    MediaProductType,
    YouTubeCampaignData,
    PlaylistingCampaignInput,
    FBLocation,
    TargetedGender,
    CurrencyCodesType,
    DefaultPixelConversionEventNames,
    InstagramAdPlacement,
} from 'modules/Types';
import {
    getCampaign_api,
    createDirectFacebookCampaign_api,
    createTiktokCampaign_v2_api,
    getConvertedCurrency_api,
    createAdBoxCampaign_api,
    AutomationFeeResponse,
    createCampaignAfterPaymentIntent_api
} from 'api/campaignApi';
import { PlaybookType, PlaybookStepName, DEFAULT_CURRENCY, MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS, MINIMUM_DAILY_BUDGET_META_PER_DAY_IN_DOLLARS, MINIMUM_LIFETIME_BUDGET_TIKTOK, PLAYBOOK_TYPE_INFO, AdPlatform } from 'modules/Const';
import { getAvailableTargetingLocations_api } from 'api/tiktokApi';
import useBrand, { BrandExistsState } from './useBrand';
import {
    CallToActionType,
    FacebookCampaignSubmissionType,
    FB_CAMPAIGNS_DEFAULT_LANGUAGES,
    FB_DEFAULT_GENDERS,
    FB_DEFAULT_MAX_AGE,
    FB_DEFAULT_MIN_AGE,
    TargetedAudience,
    TargetedAudienceType,
    TikTokCampaignSubmissionType,
    YouTubeCampaignSubmissionType,
    transformDataForCampaignSubmit,
    PlaylistingCampaignSubmissionType,
} from 'modules/Campaign';
import { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import useFacebookConnections from './useFacebookConnections';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { getOneClickCampaignDetails_api, ImportFromURLResponse } from 'api/agentApi';
import { searchFacebookGeographies } from 'api/facebookApi';
import dayjs from 'dayjs';
import { SHARED_AUDIENCE_CONFIGS } from 'modules/Const';
import useTikTokConnections from './useTikTokConnections';
import { track } from 'analytics';
import { isEmpty } from 'modules/Utils';
import { AvailablePlansForCampaignCheckout, getAvailablePlansForCampaignCheckout, calculateAutomationFee_api } from 'api/paymentsV2Api';

export const CURRENT_BUDGET_TABS = {
    LIFETIME: 'lifetime',
    DAILY: 'daily'
}

export const CURRENT_DATE_SELECTION_TABS = {
    DATE_RANGE: 'dateRange',
    NUMBER_OF_DAYS: 'numberOfDays',
    MAXIMUM_BUDGET: 'maximumBudget'
}

/** Gets the default conversion event name for a given playbook type */
export const getDefaultConversionEventName = (playbookType: PlaybookType): string | null => {
    switch (playbookType) {
        case PlaybookType.META_PRESAVES:
            return DefaultPixelConversionEventNames.PreSaveSuccess;
        case PlaybookType.META_EMAIL_SIGNUPS:
            return DefaultPixelConversionEventNames.Subscribe;
        case PlaybookType.META_TICKET_SALES:
            return DefaultPixelConversionEventNames.TourLinkClicked;
        case PlaybookType.INCREASE_MUSIC_STREAMS:
        case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
            return DefaultPixelConversionEventNames.StreamingLinkClicked;
        case PlaybookType.INCREASE_LINK_CLICKS:
            // the user must manually set the conversion event name for link clicks
            return null;
        default:
            return null;
    }
}

/** Default geography configuration for primary markets */
const DEFAULT_PRIMARY_MARKET_GEOGRAPHIES = [
    {
        id: 'US',
        name: 'United States',
        label: 'United States (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'GB',
        name: 'United Kingdom',
        label: 'United Kingdom (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'CA',
        name: 'Canada',
        label: 'Canada (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'FR',
        name: 'France',
        label: 'France (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'DE',
        name: 'Germany',
        label: 'Germany (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'AU',
        name: 'Australia',
        label: 'Australia (country)',
        type: 'country' as const,
        radius: 0,
    },
];

/** Default geography configuration for global markets */
const DEFAULT_GLOBAL_MARKET_GEOGRAPHIES = [
    {
        id: 'TR',
        name: 'Turkey',
        label: 'Turkey (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'IT',
        name: 'Italy',
        label: 'Italy (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'PL',
        name: 'Poland',
        label: 'Poland (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'SE',
        name: 'Sweden',
        label: 'Sweden (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'NO',
        name: 'Norway',
        label: 'Norway (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'MX',
        name: 'Mexico',
        label: 'Mexico (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'NL',
        name: 'Netherlands',
        label: 'Netherlands (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'PH',
        name: 'Philippines',
        label: 'Philippines (country)',
        type: 'country' as const,
        radius: 0,
    },
];

/** Represents a single geography bucket with its locations and budget */
export interface TargetedGeographyBucket {
    id: number;
    name: string;
    percentage: number;
    geographies: {
        id: string;
        name: string;
        label: string;
        type: 'country' | 'region' | 'city';
        radius?: number;
        country_code?: string
        country_name?: string
        region?: string
        region_id?: number
        supports_city?: boolean
    }[];
    excludedGeographies?: {
        id: string;
        name?: string;
        label?: string;
        type: 'country' | 'region' | 'city';
        radius?: number;
        country_code?: string
        country_name?: string
        region?: string
        region_id?: number
        supports_city?: boolean
    }[];
};
export interface CreativeSelectorPost {
    disabled?: boolean;
    originalDescription?: string;
    /** Unique identifier for the post */
    id: string;
    /** Caption or title text for the post */
    title: string;
    /** Timestamp when post was created */
    timestamp?: string;
    /** Whether post is recommended by AI */
    isRecommended?: boolean;
    /** Original post type */
    originalPostType?: AssetPlatformType | undefined;
    /** Whether post is a carousel */
    username?: string;
    /** Media type of the post */
    mediaType?: MediaType | undefined;
    /** Direct URL to video content if available */
    assetUrl?: string;
    /** Thumbnail URL specifically for videos */
    thumbnailUrl?: string;
    /** Caption for the post from the platform */
    platformCaption?: string;
    /** Descriptive text providing more information about the audience */
    description?: string;
    /** Array of tag strings associated with this audience */
    tags?: string[];
    /** Instagram ID */
    ig_id?: string;
    /** Facebook ID */
    fb_id?: string;
    /** Maximum number of items that can be selected */
    maxSelectedItems?: number;
    /** Like count */
    likesCount?: number;
    /** Comment count */
    commentsCount?: number;
    /** Share count */
    sharesCount?: number;
    /** Background color */
    backgroundColor?: string;
    /** Child images for carousel posts */
    children?: {
        id: string;
        mediaType: MediaType;
        mediaUrl: string;
    }[];
    /** Current index for carousel display */
    currentCarouselIndex?: number;

    /** Whether the post is paused */
    paused?: boolean;

    /** the link to the post on the platform */
    permalink?: string;

    /** the available ads for the post */
    availableAdPlacements?: InstagramAdPlacement[];
}

export type CurrentDateSelectionTabType = typeof CURRENT_DATE_SELECTION_TABS[keyof typeof CURRENT_DATE_SELECTION_TABS];

export const DEFAULT_TIKTOK_GEOGRAPHIES: TargetedGeographyBucket[] = [
    {
        id: 1,
        name: "Primary Targeting",
        percentage: 100,
        geographies: [
            {
                id: "6252001",
                name: "United States",
                label: "United States (Country)",
                type: "country",
                country_code: "US",
                country_name: "United States",
                region: "United States",
                region_id: 1,
                supports_city: false
            },
            {
                id: "6251999",
                name: "Canada",
                label: "Canada (Country)",
                type: "country",
                country_code: "CA",
                country_name: "Canada",
                region: "Canada",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2635167",
                name: "United Kingdom",
                label: "United Kingdom (Country)",
                type: "country",
                country_code: "UK",
                country_name: "United Kingdom",
                region: "United Kingdom",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2077456",
                name: "Australia",
                label: "Australia (Country)",
                type: "country",
                country_code: "AU",
                country_name: "Australia",
                region: "Australia",
                region_id: 1,
                supports_city: false
            },
            {
                id: "3017382",
                name: "France",
                label: "France (Country)",
                type: "country",
                country_code: "FR",
                country_name: "France",
                region: "France",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2921044",
                name: "Germany",
                label: "Germany (Country)",
                type: "country",
                country_code: "DE",
                country_name: "Germany",
                region: "Germany",
                region_id: 1,
                supports_city: false
            }
        ]
    }
]

/** Interface defining campaign goal configuration */
export interface CampaignGoal {
    /** Unique identifier for the goal */
    id: string;
    /** Display label for the goal */
    label: string;
    /** Icon identifier for visual representation */
    icon: string;
    /** Detailed description of what the goal achieves */
    description: string;
    /** Recommended usage of this goal */
    recommendation: string;
    /** Specific conversion goal identifier */
    conversionGoal: string;
    /** Helpful tips for achieving this goal */
    tips: string;
    /** Optional array of use cases for this goal */
    whenToUse?: string[];
    /** Optional array of required setup steps */
    setupNeeded?: string[];
    /** Type of conversion tracking */
    conversionType: string;
    /** Expected average cost per conversion */
    averageConversionCost: number;
    /** Whether this goal is currently available */
    available?: boolean;
    /** Note explaining availability status if applicable */
    availabilityNote?: string;
    /** Whether this goal is currently available */
    subtype?: string;
}

export type Caption = {
    id: number;
    text: string;
    aiGenerated?: boolean;
}

/** Type for budget tab options */
export type CurrentBudgetTabType = typeof CURRENT_BUDGET_TABS[keyof typeof CURRENT_BUDGET_TABS];

/** Interface defining the campaign state and actions */
export interface CampaignState {
    /** Campaign daily budget in USD */
    budget: string;
    /** Campaign maximum budget in USD */
    budgetMaximum: string;
    /** Campaign start date in ISO format */
    startDate: string | null;
    /** Campaign end date in ISO format */
    endDate: string | null;
    /** Type of playbook being used */
    playbookType: PlaybookType | null;
    /** Type of ad platform being used */
    adPlatform: AdPlatform | null;
    /** Current campaign details */
    campaign: CampaignDetails | null;

    showOneClickCampaignModal: boolean;
    showOneClickCampaignSetupModal: boolean;
    showCreateNewCampaignModal: boolean;
    newCampaignAdPlatformFilter: AdPlatform | null;
    captions: Caption[];
    /** User-defined campaign name */
    campaignName: string;
    /** Loading state for campaign operations */
    isLoadingCampaign: boolean;
    /** Loading state for campaign submission */
    isSubmittingCampaign: boolean;
    isLoadingSocialItems: boolean;
    /** Total duration of campaign in days */
    numberOfDays: number;
    /** Selected campaign goal configuration */
    campaignGoal: CampaignGoal | null;
    /** Current step in the playbook setup process */
    currentStep: PlaybookStepName | undefined;
    /** Selected call-to-action value for TikTok ads */
    callToAction: CallToActionType;
    selectedInstagramPosts: InstagramPost[]
    uploadedAssets: CloudinaryUploadedFileType[]
    /** Selected spark codes for TikTok ads */
    selectedSparkCodes: TikTokSparkAd[];
    geographyBuckets: TargetedGeographyBucket[];
    setGeographyBuckets: (geographyBuckets: TargetedGeographyBucket[]) => void;
    showEditGeographyBucketsBanner: boolean;
    setShowEditGeographyBucketsBanner: (show: boolean) => void;
    isGeographyBucketsObjectValid: boolean;
    validateGeographyBuckets: () => void;
    cleanupEmptyGeographyBuckets: () => void;
    /** Loading state for geography search */
    isLoadingGeographies: boolean;
    /** Selected audience groups for campaign targeting */
    selectedAudienceGroups: TargetingAudienceGroup[];
    /** Minimum age for campaign targeting */
    minAge: number;
    /** Maximum age for campaign targeting */
    maxAge: number;

    /** Gender targeting setting */
    genders: TargetedGender[];
    /** Selected language locales for targeting */
    languages: TargetedLanguage[];
    /** Whether demographics targeting is automated */
    isDemographicsAutomated: boolean;
    /** Previewed asset for TikTok ads */
    previewedAsset: CreativeSelectorPost | null;
    /** Current budget tab */
    currentBudgetTab: CurrentBudgetTabType;
    /** Current date selection tab */
    currentDateSelectionTab: CurrentDateSelectionTabType;
    isGeneratingOneClickCampaign: boolean;
    campaignDisclaimer: string;

    selectedSongToPromote: RecordDetails | null;
    highlightedTrack: {
        id: string;
        uri: string;
        name: string;
    } | null;
    isLoadingMaestroRecommendations: boolean;

    playbookStepNames: PlaybookStepName[];

    linksToPromote: ExternalLink[];
    enabledPlatforms: {
        url: string;
        name: string;
    }[]
    conversionsEnabled: boolean;
    conversionsEventName?: string;

    instagramPosts: InstagramPost[];
    releases: RecordDetails[];
    tikTokPosts: any[];
    youTubeVideos: any[];
    importedFromUrlPosts: ImportFromURLResponse[];
    campaignInitialized: boolean;

    // Actions
    /** Updates the campaign budget */
    setBudget: (budget: string) => void;
    /** Updates the campaign maximum budget */
    setBudgetMaximum: (budgetMaximum: string) => void;
    setCaptions: (captions: Caption[]) => void;
    updateCaption: (captionId: number, text: string) => void;
    /** Updates the campaign goal */
    setCampaignGoal: (goal: CampaignGoal | null) => void;
    /** Updates the campaign start date */
    setStartDate: (startDate: string) => void;
    /** Updates the campaign end date */
    setEndDate: (endDate: string) => void;
    /** Updates the playbook type */
    setPlaybookType: (playbookType: PlaybookType) => void;
    /** Updates the ad platform */
    setAdPlatform: (adPlatform: AdPlatform | null) => void;
    /** Updates the loading state */
    setIsLoadingCampaign: (isLoadingCampaign: boolean) => void;
    setIsLoadingSocialItems: (isLoadingSocialItems: boolean) => void;
    /** Updates the loading state for campaign submission */
    setIsSubmittingCampaign: (isSubmittingCampaign: boolean) => void;
    /** Updates the previewed asset */
    setPreviewedAsset: (asset: CreativeSelectorPost | null, chooseNextAsset?: boolean) => void;
    /** Updates the selected audience groups */
    setSelectedAudienceGroups: (audienceGroups: TargetingAudienceGroup[]) => void;
    /** Removes a tag from an audience group */
    removeInterestFromAudienceGroup: (audienceGroupId: string, interestId: number) => void;
    /** Adds a tag to an audience group */
    addInterestToAudienceGroup: (audienceGroupId: string, interest: SearchPlatformInterestsResult) => void;
    /** Removes an audience group */
    removeAudienceGroup: (audienceGroupId: string) => void;
    /** Updates the campaign duration in days */
    setNumberOfDays: (numberOfDays: number) => void;
    /** Updates the campaign details */
    setCampaign: (campaign: CampaignDetails) => void;
    /** Updates the show one click campaign modal */
    setShowOneClickCampaignModal: (show: boolean) => void;
    /** Updates the show one click campaign setup modal */
    setShowOneClickCampaignSetupModal: (show: boolean) => void;
    /** Updates the show create new campaign modal */
    setShowCreateNewCampaignModal: (show: boolean) => void;
    setNewCampaignAdPlatformFilter: (adPlatform: AdPlatform | null) => void;
    /** Updates the campaign name */
    setCampaignName: (campaignName: string) => void;
    /** Fetches campaign details by ID */
    getCampaign: (campaignId: number) => Promise<CampaignDetails | null>;
    /** Creates a new campaign for a brand */
    createCampaign: (brandId: string) => Promise<CampaignDetails>;
    /** Updates the current playbook step */
    setCurrentStep: (step: PlaybookStepName) => void;
    /** Updates the playbook step names */
    setPlaybookStepNames: (playbookStepNames: PlaybookStepName[]) => void;
    /** Updates the selected call-to-action */
    setCallToAction: (callToAction: CallToActionType) => void;
    /** Updates the selected spark codes */
    setSelectedSparkCodes: (sparkCodes: TikTokSparkAd[]) => void;
    /** Adds a spark code to the selected spark codes */
    addSparkCode: (sparkCode: TikTokSparkAd) => boolean;
    /** Removes a specific spark code */
    removeSparkCode: (sparkCodeId: string) => void;
    /** Searches for available geographies */
    searchGeographies: (query: string, platform: 'tiktok' | 'facebook') => Promise<TikTokTargetingGeography[] | FBLocation[]>;
    /** Updates the minimum targeting age */
    setMinAge: (age: number) => void;
    /** Updates the maximum targeting age */
    setMaxAge: (age: number) => void;
    /** Updates both minimum and maximum age */
    setAgeRange: (minAge: number, maxAge: number) => void;
    /** Updates the gender targeting setting */
    setTargetedGender: (gender: TargetedGender[]) => void;
    /** Updates the language locales */
    setLanguages: (locales: TargetedLanguage[]) => void;
    /** Updates the demographics automation setting */
    setIsDemographicsAutomated: (isAutomated: boolean) => void;
    /** Gets all selected posts in the transformed format */
    getAllSelectedPosts: (variant: 'facebook' | 'tiktok') => CreativeSelectorPost[];
    /** Updates multiple demographics settings */
    setDemographics: (demographics: Partial<{
        minAge: number;
        maxAge: number;
        genders: TargetedGender[];
        locales: TargetedLanguage[];
        isAutomated: boolean;
    }>) => void;

    setIsLoadingMaestroRecommendations: (isLoading: boolean) => void;

    /** Updates the current budget tab */
    setCurrentBudgetTab: (tab: CurrentBudgetTabType) => void;
    /** Updates the current date selection tab */
    setCurrentDateSelectionTab: (tab: CurrentDateSelectionTabType) => void;
    /** Updates the playbook steps after finalization */
    setPlaybookSteps: (steps: PlaybookStep[]) => void;
    /** Updates the link to promote */
    setLinksToPromote: (links: ExternalLink[]) => void;
    /** Updates the enabled platforms */
    setEnabledPlatforms: (platforms: {
        url: string;
        name: string;
    }[]) => void;

    setSelectedSongToPromote: (songToPromote: RecordDetails) => void;
    setHighlightedTrack: (track: {
        id: string;
        uri: string;
        name: string;
    }) => void;
    getDataForDebugBox: () => Record<string, any>;

    nextCampaignStep: () => void;
    prevCampaignStep: () => void;
    setSelectedInstagramPosts: (posts: InstagramPost[]) => void;
    removeSelectedInstagramPost: (postId: string) => void;
    setUploadedAssets: (assets: CloudinaryUploadedFileType[]) => void;
    getUploadedAssets: () => CloudinaryUploadedFileType[];
    removeUploadedAsset: (assetId: string) => void;
    setImportedFromUrlPosts: (posts: ImportFromURLResponse[]) => void;
    removeImportedFromUrlPost: (postId: string) => void;
    submitCampaignFromAdBox: () => Promise<any>;
    submitCampaign: ({
        submitType,
        recurring_interval,
        overrideCampaignInput
    }: {
        submitType: 'directCreate' | 'payment_intent',
        recurring_interval?: BillingCycle,
        overrideCampaignInput?: YouTubeCampaignData | PlaylistingCampaignInput | PlaylistingCampaignSubmissionType
    }) => Promise<any>;
    resetDemographicsToRecommended: () => void;
    resetAudiencesToRecommended: () => void;
    getDataForSubmission: () => FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | YouTubeCampaignData | PlaylistingCampaignSubmissionType;
    campaignAllowsDirectPayment: () => boolean;
    campaignDoesntRequireUpgrade: () => boolean;
    /** Resets all state values to their defaults */
    resetCampaignState: () => void;
    initializeCampaignState: ({
        playbookType,
        currentStep,
        playbookStepNames
    }: {
        playbookType: PlaybookType;
        currentStep?: PlaybookStepName;
        playbookStepNames?: PlaybookStepName[];
    }) => void;
    setConversionsEnabled: (enabled: boolean) => void;
    setConversionsEventName: (event: string) => void;
    /** Preserves existing audience groups when initializing campaign state */
    preserveLoadedAudienceGroups: (preserveExisting?: boolean) => TargetingAudienceGroup[];
    /** Resets geography buckets to default values */
    resetGeographyBuckets: (multipleLocations?: boolean) => void;
    /** Returns campaign data in format needed for submission */

    getInstagramPosts: () => Promise<InstagramPost[]>;
    getReleases: () => Promise<RecordDetails[]>;
    getTikTokPosts: () => Promise<any[]>;
    getYouTubeVideos: () => Promise<any[]>;

    selectedInstagramPost: InstagramPost | null;
    selectedRelease: RecordDetails | null;
    selectedTikTokPost: any | null;
    selectedYouTubeVideo: any | null;
    setSelectedInstagramPost: (post: InstagramPost | null) => void;
    setSelectedRelease: (release: RecordDetails | null) => void;
    setSelectedTikTokPost: (post: any | null) => void;
    setSelectedYouTubeVideo: (video: any | null) => void;

    isBudgetTooLow: boolean;
    /** Current automation fee calculation data */
    automationFeeData: AutomationFeeResponse | null;
    /** Loading state for automation fee calculation */
    isLoadingAutomationFee: boolean;
    /** Campaign plan details data for new users */
    campaignPlanDetailsData: AvailablePlansForCampaignCheckout | null;
    /** Loading state for campaign plan details calculation */
    isLoadingCampaignPlanDetails: boolean;
    /** Confirmation checkbox for TikTok/YouTube campaigns */
    campaignConfirmationChecked: boolean;
    /** Get automation fee calculation for current campaign settings */
    getAutomationFee: () => Promise<void>;
    /** Set automation fee data */
    setAutomationFeeData: (data: AutomationFeeResponse | null) => void;
    /** Set loading state for automation fee calculation */
    setIsLoadingAutomationFee: (loading: boolean) => void;
    /** Set campaign confirmation checkbox state */
    setCampaignConfirmationChecked: (checked: boolean) => void;
    /** Get confirmation disclaimer text for current campaign */
    getConfirmationDisclaimerText: () => string | null;
    /** Get whether confirmation checkbox is required for current campaign */
    isConfirmationRequired: () => boolean;
    generateOneClickCampaign: () => Promise<void>;
    checkIsAdBudgetTooLow: () => Promise<void>;
    getTotalBudget: () => {
        totalBudget: number;
        isValid: boolean;
        budgetType: string;
    };
    /** Get minimum daily budget based on current ad platform */
    getMinimumDailyBudget: () => number;
    /** Calculate campaign length in days based on current date selection method */
    getCampaignLength: () => number;
    resetSelectedPosts: () => void;
    usesExpressSetup: () => boolean;

    /** Campaign plan details data for new users */
    getAvailablePlansforCampaign: () => Promise<void>;
    setCampaignPlanDetailsData: (data: AvailablePlansForCampaignCheckout | null) => void;
    setIsLoadingCampaignPlanDetails: (loading: boolean) => void;
    readyForSubmission?: boolean; // Added for agent campaign status
    shouldHideBudgetSlider: () => boolean;
    // New action for setting state from AgentCampaign
    setCampaignDataFromAgent: (agentCampaign: AgentCampaignData) => void;

    // Playlisting-specific fields
    /** Pitch notes for playlisting campaigns */
    pitchNotes: string;
    /** Selected tier for playlisting campaigns */
    selectedPlaylistingTier: {
        tier: number;
        cost: number;
        proDiscount: number;
        bgImage: string;
    } | null;
    /** Set pitch notes */
    setPitchNotes: (notes: string) => void;
    /** Set selected playlisting tier */
    setSelectedPlaylistingTier: (tier: {
        tier: number;
        cost: number;
        proDiscount: number;
        bgImage: string;
    } | null) => void;
}

export interface CampaignExistsState extends CampaignState {
    playbookType: PlaybookType;
    adPlatform: AdPlatform;
}

// Interfaces for AgentCampaign (mirrored from AssistantWorkspaceCampaignApproval)
// TODO: Consider moving these to a shared types file if used elsewhere
export interface AgentCreativeSourceData {
    type: 'instagram_post' | 'uploaded_asset' | 'spark_code' | 'external_image';
    postUrl?: string;
    postId?: string;
    mediaUrl: string;
    thumbnailUrl: string;
    cloudinaryId?: string;
    wasAiGenerated?: boolean;
    assetType?: 'image' | 'video';
    sparkCode?: string;
    // Simplified user_info, video_info, item_info for brevity
    user_info?: { identity_display_name?: string; identity_info?: { avatar_url?: string } };
    video_info?: { preview_url?: string; poster_url?: string; video_id?: string };
    item_info?: { item_id?: string; text?: string };
}

export interface AgentCreativeVariationData {
    source: AgentCreativeSourceData;
    caption: string;
    cta: CallToActionType;
}

export interface AgentCampaignInputData {
    playbookType: PlaybookType;
    campaignName: string;
    goalLink: string;
    platform: AdPlatform;
    budget: number;
    totalAdsBudget: number;
    budgetType: 'daily' | 'lifetime';
    length: {
        startDate: string;
        endDate: string;
    };
    creatives: AgentCreativeVariationData[];
    conversionEventName?: string;
}

export interface AgentCampaignData {
    id: string;
    name: string; // Consider removing if always same as campaign_input.campaignName
    playbook_type: string; // Will be mapped to PlaybookType enum
    status?: string;
    goal_link?: string; // Consider removing if always same as campaign_input.goalLink
    agent_notes?: {
        targeted_audiences?: string[];
        targeted_locations?: string[];
        [key: string]: any; // Other notes
    };
    campaign_input: AgentCampaignInputData;
    readyForSubmission?: boolean;
    checkoutDetails?: any; // Use 'any' for now, can be typed later if needed
}
// End of AgentCampaign interfaces


/** Campaign management hook using Zustand store */
const useCampaignBuilder = create(
    immer<CampaignState>((set, get) => ({
        budget: '100',
        budgetMaximum: '100',
        startDate: '',
        endDate: '',
        // playbookType: PlaybookType.INCREASE_MUSIC_STREAMS,
        playbookType: null,
        adPlatform: null,
        campaign: null,
        showOneClickCampaignModal: false,
        showOneClickCampaignSetupModal: false,
        campaignName: '',
        isLoadingCampaign: false,
        isSubmittingCampaign: false,
        isLoadingSocialItems: false,
        highlightedTrack: null,
        numberOfDays: 0,
        selectedInstagramPosts: [],
        campaignInitialized: false,
        captions: [],
        showCreateNewCampaignModal: false,
        newCampaignAdPlatformFilter: AdPlatform.META,
        campaignDisclaimer: '',
        importedFromUrlPosts: [],
        setCaptions: (captions: Caption[]) => { set(state => { state.captions = captions }) },
        updateCaption: (captionId: number, text: string) => { set(state => { state.captions = state.captions.map(c => c.id === captionId ? { ...c, caption: text } : c) }) },
        uploadedAssets: [],
        currentStep: undefined,
        callToAction: { value: 'LEARN_MORE', label: 'Learn More' },
        selectedSparkCodes: [],
        geographyBuckets: [{
            id: 1,
            name: '',
            percentage: 100,
            geographies: []
        }],
        showEditGeographyBucketsBanner: false,
        isGeographyBucketsObjectValid: true,
        isLoadingGeographies: false,
        targetedGeographies: [],
        campaignGoal: null,
        isGeneratingOneClickCampaign: false,
        minAge: 18,
        selectedAudienceGroups: [],
        maxAge: 65,
        genders: [],
        languages: [],
        isDemographicsAutomated: true,
        previewedAsset: null,
        currentBudgetTab: CURRENT_BUDGET_TABS.LIFETIME,
        currentDateSelectionTab: CURRENT_DATE_SELECTION_TABS.DATE_RANGE,
        playbookStepNames: [],
        linksToPromote: [],
        conversionsEnabled: false,
        conversionsEventName: undefined,
        instagramPosts: [],
        releases: [],
        tikTokPosts: [],
        youTubeVideos: [],

        selectedInstagramPost: null,
        selectedRelease: null,
        selectedTikTokPost: null,
        selectedYouTubeVideo: null,
        isBudgetTooLow: false,
        automationFeeData: null,
        isLoadingAutomationFee: false,
        campaignPlanDetailsData: null,
        isLoadingCampaignPlanDetails: false,
        campaignConfirmationChecked: false,
        
        // Playlisting-specific state
        pitchNotes: '',
        selectedPlaylistingTier: null,
        
        /** for conversions campaigns, this is the platforms that are enabled 
        * on the link
        */
        shouldHideBudgetSlider: () => {
            return true
        },
        usesExpressSetup: () => {
            const { adPlatform } = get();
            if (adPlatform === AdPlatform.TIKTOK) {
                return true
            }

            const { selectedFacebookPage, selectedFacebookAdAccount } = useFacebookConnections.getState()
            return !(selectedFacebookPage && selectedFacebookAdAccount)
        },

        setConversionsEventName: (event: string) => { set(state => { state.conversionsEventName = event }) },
        setConversionsEnabled: (enabled: boolean) => {
            const { selectedAudienceGroups, setSelectedAudienceGroups, adPlatform, usesExpressSetup } = get();
            const { selectedFacebookPixel } = useFacebookConnections.getState();

            set(state => { state.conversionsEnabled = enabled });

            // Only sync audiences for Facebook ad campaigns
            if (adPlatform !== AdPlatform.META) {
                return
            };

            // Handle conversion audience groups
            const conversionEventLookalikesAudienceId = SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id;
            const isExpressFlow = usesExpressSetup();

            if (enabled && (selectedFacebookPixel?.id || isExpressFlow)) {
                // Add conversion lookalikes if conversions enabled and either:
                // - User has connected their own pixel (custom setup), OR
                // - User is on express setup (Symphony handles pixel)
                const audienceExists = selectedAudienceGroups.some(group =>
                    group.id === conversionEventLookalikesAudienceId
                );

                if (!audienceExists) {
                    setSelectedAudienceGroups([
                        ...selectedAudienceGroups,
                        SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES
                    ]);
                }
            } else if (!enabled && !isExpressFlow) {
                // Only remove conversion lookalikes if:
                // - Conversions are disabled AND
                // - User is NOT on express setup
                // (Express setup always has conversions through Symphony's pixel)
                const updatedGroups = selectedAudienceGroups.filter(group =>
                    group.id !== conversionEventLookalikesAudienceId
                );

                // Only update if something was removed
                if (updatedGroups.length !== selectedAudienceGroups.length) {
                    setSelectedAudienceGroups(updatedGroups);
                }
            }
            // If user is on express setup, never remove conversion lookalikes
        },
        enabledPlatforms: [],

        selectedSongToPromote: null,
        isLoadingMaestroRecommendations: false,

        campaignAllowsDirectPayment: () => {
            const { playbookType, adPlatform } = get();
            const { selectedFacebookPage, selectedFacebookAdAccount } = useFacebookConnections.getState()
            if (playbookType === PlaybookType.INCREASE_VIDEO_VIEWS || playbookType === PlaybookType.GET_PLAYLISTED) {
                return true
            }

            if (adPlatform === AdPlatform.TIKTOK) {
                return true
            }

            if (!selectedFacebookPage || !selectedFacebookAdAccount) {
                return true
            }

            return false
        },

        campaignDoesntRequireUpgrade: () => {
            const { playbookType } = get();
            return playbookType === PlaybookType.GET_PLAYLISTED;
        },

        removeInterestFromAudienceGroup: (audienceGroupId: string, interestId: number) => {
            set(state => {
                state.selectedAudienceGroups = state.selectedAudienceGroups.map(group => {
                    if (group.id === audienceGroupId) {
                        group.tags = group.tags.filter(tag => tag.id !== interestId);
                    }
                    return group;
                });
            });
        },
        setLinksToPromote: (links: ExternalLink[]) => { set(state => { state.linksToPromote = links }) },
        setEnabledPlatforms: (platforms: {
            url: string;
            name: string;
        }[]) => { set(state => { state.enabledPlatforms = platforms }) },
        addInterestToAudienceGroup: (audienceGroupId: string, interest: SearchPlatformInterestsResult) => {
            set(state => {
                // Check if audience group exists
                const groupExists = state.selectedAudienceGroups.some(group => group.id === audienceGroupId);

                if (!groupExists) {
                    // Create new audience group if it doesn't exist
                    // Convert id from camelCase to title case for display
                    const title = audienceGroupId
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase());

                    state.selectedAudienceGroups.push({
                        id: audienceGroupId,
                        title,
                        tags: []
                    });
                }

                // Add interest to the group
                state.selectedAudienceGroups = state.selectedAudienceGroups.map(group => {
                    if (group.id === audienceGroupId) {
                        group.tags = [...group.tags, {
                            id: Number(interest.id),
                            title: interest.name,
                            audience_size: interest.audience_size,
                            type: interest.type as TargetedAudienceType
                        }];
                    }
                    return group;
                });
            });
        },

        removeAudienceGroup: (audienceGroupId: string) => {
            set(state => {
                state.selectedAudienceGroups = state.selectedAudienceGroups.filter(group => group.id !== audienceGroupId);
            });
        },

        /** Get automation fee calculation for current campaign settings */
        getAutomationFee: async (): Promise<void> => {
            const {
                budget,
                adPlatform,
                usesExpressSetup,
                playbookType
            } = get();

            const { currentBrand } = useBrand.getState() as BrandExistsState;
            const { selectedFacebookAdAccount } = useFacebookConnections.getState();

            // Don't calculate fees if essential data is missing
            if (!budget || !adPlatform || !currentBrand?.id) {
                set(state => {
                    state.automationFeeData = null;
                    state.isLoadingAutomationFee = false;
                });
                return;
            }

            const budgetNumber = Number(budget);
            if (isNaN(budgetNumber) || budgetNumber <= 0) {
                set(state => {
                    state.automationFeeData = null;
                    state.isLoadingAutomationFee = false;
                });
                return;
            }

            // Map AdPlatform to API platform type
            let platformType: 'META' | 'YOUTUBE' | 'TIKTOK' | 'PLAYLISTS';
            switch (adPlatform) {
                case AdPlatform.META:
                    platformType = 'META';
                    break;
                case AdPlatform.TIKTOK:
                    platformType = 'TIKTOK';
                    break;
                case AdPlatform.YOUTUBE:
                    platformType = 'YOUTUBE';
                    break;
                case AdPlatform.PLAYLISTS:
                    platformType = 'PLAYLISTS';
                    break;
                default:
                    // Default to META for unsupported platforms
                    platformType = 'META';
            }

            const currency = selectedFacebookAdAccount?.currency || 'USD';
            const isExpressSetup = usesExpressSetup();

            try {
                set(state => { state.isLoadingAutomationFee = true });

                // Use enhanced paymentV2 API for unified fee calculation
                // Backend automatically determines playlisting vs automation fee logic
                const feeData = await calculateAutomationFee_api(currentBrand.id, {
                    adPlatform: platformType,
                    budget: budgetNumber,
                    currency,
                    usesExpressSetup: isExpressSetup,
                    playbookType: playbookType || undefined
                });

                set(state => { state.automationFeeData = feeData });
            } catch (error) {
                console.error('Error getting automation fee:', error);
                set(state => { state.automationFeeData = null });
            } finally {
                set(state => { state.isLoadingAutomationFee = false });
            }
        },

        /** Set automation fee data */
        setAutomationFeeData: (data: AutomationFeeResponse | null) => {
            set(state => { state.automationFeeData = data });
        },

        /** Set loading state for automation fee calculation */
        setIsLoadingAutomationFee: (loading: boolean) => {
            set(state => { state.isLoadingAutomationFee = loading });
        },

        checkIsAdBudgetTooLow: async (): Promise<void> => {
            const {
                budget,
                startDate,
                endDate,
                numberOfDays,
                currentBudgetTab,
                currentDateSelectionTab,
                getTotalBudget,
                playbookType,
                getCampaignLength
            } = get();

            const {
                selectedFacebookAdAccount
            } = useFacebookConnections.getState();

            const budgetNumber = Number(budget);

            if (isEmpty(budget) || isNaN(budgetNumber) || budgetNumber <= 0) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }

            if (playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
                set(state => { state.isBudgetTooLow = budgetNumber < MINIMUM_LIFETIME_BUDGET_TIKTOK });
                return;
            }

            const adPlatformCurrency = selectedFacebookAdAccount?.currency || DEFAULT_CURRENCY;

            const isDefaultCurrency = adPlatformCurrency === DEFAULT_CURRENCY;

            // Determine minimum budget based on ad platform
            const adPlatform = PLAYBOOK_TYPE_INFO[playbookType as PlaybookType]?.adPlatform;
            let minimumDailyBudget = 10;
            switch (adPlatform) {
                case AdPlatform.META:
                    minimumDailyBudget = MINIMUM_DAILY_BUDGET_META_PER_DAY_IN_DOLLARS;
                    break;
                case AdPlatform.TIKTOK:
                    minimumDailyBudget = MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS;
                    break;
                case AdPlatform.YOUTUBE:
                    minimumDailyBudget = 20;
                    break;
                default:
                    minimumDailyBudget = 10;
            }

            console.log('minimumDailyBudget', minimumDailyBudget);

            if (!isDefaultCurrency) {
                try {
                    minimumDailyBudget = await getConvertedCurrency_api({
                        from: 'USD',
                        to: adPlatformCurrency as CurrencyCodesType,
                        amount: minimumDailyBudget
                    });
                } catch (error) {
                    console.error('Error converting currency for minimum budget:', error);
                    // Fallback to USD amount if conversion fails
                    minimumDailyBudget = 10;
                }
            }

            if (budgetNumber < minimumDailyBudget) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }

            let calculatedDurationDays = getCampaignLength();

            // Handle special case for maximum budget
            if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET) {
                // For maximum budget, we need to ensure daily budget is at least minimum
                const maxDays = Math.floor(budgetNumber / minimumDailyBudget);

                // If budget can't even support one day at minimum rate, it's invalid
                set(state => { state.isBudgetTooLow = maxDays < 1 });
                return
            }

            // If we couldn't determine duration or it's invalid, budget is invalid
            if (calculatedDurationDays <= 0) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }
            // Calculate daily budget based on total budget and duration
            const dailyBudget = budgetNumber / calculatedDurationDays;

            set(state => { state.isBudgetTooLow = dailyBudget < minimumDailyBudget });
        },

        getTotalBudget: (): {
            totalBudget: number;
            isValid: boolean;
            budgetType: string;
        } => {
            const {
                budget,
                currentBudgetTab,
            } = get();

            const LIFETIME_BUDGET_THRESHOLD = 100;
            const DAILY_BUDGET_THRESHOLD = 10;
            const budgetNumber = Number(budget);

            // If budget is not a valid number, it's invalid
            if (!budget || budget === '' || isNaN(budgetNumber) || budgetNumber <= 0) {
                return {
                    totalBudget: 0,
                    isValid: false,
                    budgetType: currentBudgetTab
                };
            }

            // Case 1: Daily budget
            if (currentBudgetTab === CURRENT_BUDGET_TABS.DAILY) {
                return {
                    totalBudget: budgetNumber,
                    isValid: budgetNumber > DAILY_BUDGET_THRESHOLD,
                    budgetType: 'daily'
                };
            }

            // Case 2: Lifetime budget
            if (currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME) {
                // Calculate total lifetime budget based on the selected date option
                let totalLifetimeBudget = budgetNumber;

                // If using number of days, we could calculate total budget as daily × days
                // but since we already have the lifetime budget directly, we just use it

                return {
                    totalBudget: totalLifetimeBudget,
                    isValid: totalLifetimeBudget >= LIFETIME_BUDGET_THRESHOLD,
                    budgetType: 'lifetime'
                };
            }

            // Default fallback (should not reach here in normal operation)
            return {
                totalBudget: budgetNumber,
                isValid: false,
                budgetType: currentBudgetTab
            };
        },

        /** Get minimum daily budget based on current ad platform */
        getMinimumDailyBudget: (): number => {
            const { adPlatform } = get();
            switch (adPlatform) {
                case AdPlatform.META:
                    return MINIMUM_DAILY_BUDGET_META_PER_DAY_IN_DOLLARS;
                case AdPlatform.TIKTOK:
                    return MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS;
                case AdPlatform.YOUTUBE:
                    return 20;
                default:
                    return 10;
            }
        },

        /** Calculate campaign length in days based on current date selection method */
        getCampaignLength: (): number => {
            const {
                startDate,
                endDate,
                numberOfDays,
                currentDateSelectionTab
            } = get();

            if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.DATE_RANGE) {
                if (!startDate || !endDate) {
                    return 1; // Return 1 instead of 0 to prevent division by zero
                }
                // Calculate inclusive duration (both start and end dates count)
                const calculatedDays = dayjs(endDate).diff(dayjs(startDate), 'days') + 1;
                // Ensure minimum of 1 day even if same start/end date
                return Math.max(calculatedDays, 1);
            } else if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS) {
                // Ensure minimum of 1 day to prevent division by zero
                return Math.max(numberOfDays || 1, 1);
            }

            return 1; // Return 1 instead of 0 to prevent division by zero
        },

        setSelectedAudienceGroups: (audienceGroups: TargetingAudienceGroup[]) => { set(state => { state.selectedAudienceGroups = audienceGroups }) },

        setPreviewedAsset: (asset: CreativeSelectorPost | null, chooseNextAsset: boolean = true) => {
            const {
                uploadedAssets,
                selectedInstagramPosts,
                selectedSparkCodes,
                previewedAsset
            } = get();

            if (asset === null) {
                // If we want to choose the next asset
                if (chooseNextAsset) {
                    // Try to get first uploaded asset or instagram post
                    const nextAsset = uploadedAssets[0] || selectedInstagramPosts[0] || null;

                    if (nextAsset) {
                        const transformedPost = transformPostsToCreativeFormat([nextAsset])[0];
                        set(state => { state.previewedAsset = transformedPost });
                        return;
                    }
                }

                set(state => { state.previewedAsset = null });
                return;
            }

            set(state => { state.previewedAsset = asset });
        },
        setCampaignGoal: (goal: CampaignGoal | null) => { set(state => { state.campaignGoal = goal }) },
        setBudget: (budget: string) => { set(state => { state.budget = budget }) },
        setBudgetMaximum: (budgetMaximum: string) => { set(state => { state.budgetMaximum = budgetMaximum }) },
        setStartDate: (startDate: string) => { set(state => { state.startDate = startDate }) },
        setEndDate: (endDate: string) => { set(state => { state.endDate = endDate }) },
        setPlaybookType: (playbookType: PlaybookType) => { set(state => { state.playbookType = playbookType }) },
        setAdPlatform: (adPlatform: AdPlatform | null) => { set(state => { state.adPlatform = adPlatform }) },
        setIsLoadingCampaign: (isLoadingCampaign: boolean) => { set(state => { state.isLoadingCampaign = isLoadingCampaign }) },
        setIsSubmittingCampaign: (isSubmittingCampaign: boolean) => { set(state => { state.isSubmittingCampaign = isSubmittingCampaign }) },
        setIsLoadingSocialItems: (isLoadingSocialItems: boolean) => { set(state => { state.isLoadingSocialItems = isLoadingSocialItems }) },
        setCampaign: (campaign: CampaignDetails) => { set(state => { state.campaign = campaign }) },
        setShowCreateNewCampaignModal: (show: boolean) => { set(state => { state.showCreateNewCampaignModal = show }) },
        setShowOneClickCampaignModal: (show: boolean) => { set(state => { state.showOneClickCampaignModal = show }) },
        setShowOneClickCampaignSetupModal: (show: boolean) => {
            const { resetCampaignState } = get();
            set(state => {
                state.showOneClickCampaignSetupModal = show
            })
            if (!show) {
                resetCampaignState();
            }
        },
        setNewCampaignAdPlatformFilter: (adPlatform: AdPlatform | null) => { set(state => { state.newCampaignAdPlatformFilter = adPlatform }) },
        setCampaignName: (campaignName: string) => { set(state => { state.campaignName = campaignName }) },
        setNumberOfDays: (numberOfDays: number) => { set(state => { state.numberOfDays = numberOfDays }) },
        setCurrentStep: (step: PlaybookStepName) => { set(state => { state.currentStep = step }) },
        setPlaybookStepNames: (playbookStepNames: PlaybookStepName[]) => { set(state => { state.playbookStepNames = playbookStepNames; }); },
        setCallToAction: (callToAction: CallToActionType) => { set(state => { state.callToAction = callToAction }) },
        setSelectedInstagramPosts: (posts: InstagramPost[]) => {
            set(state => {
                state.selectedInstagramPosts = posts
            })
        },
        setImportedFromUrlPosts: (posts: ImportFromURLResponse[]) => {
            set(state => {
                state.importedFromUrlPosts = posts
            })
        },
        removeImportedFromUrlPost: (postId: string) => {
            set(state => {
                state.importedFromUrlPosts = state.importedFromUrlPosts.filter(p => p.id !== postId)
            })
        },
        setUploadedAssets: (assets: CloudinaryUploadedFileType[]) => {
            const { captions } = get();
            let updatedCaptions = captions;
            if (captions.length == 0) {
                updatedCaptions = [{
                    id: 1,
                    text: '',
                    aiGenerated: false
                }];
            }
            set(state => {
                state.uploadedAssets = assets;
                state.captions = updatedCaptions;
            })
        },
        getUploadedAssets: () => {
            return get().uploadedAssets;
        },
        removeUploadedAsset: (assetId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            set(state => { state.uploadedAssets = state.uploadedAssets.filter(a => a.id !== assetId) })

            if (previewedAsset?.id === assetId) {
                setPreviewedAsset(null)
            }
        },
        removeSelectedInstagramPost: (postId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            set(state => { state.selectedInstagramPosts = state.selectedInstagramPosts.filter(p => p.id !== postId) })

            if (previewedAsset?.id === postId) {
                setPreviewedAsset(null)
            }
        },

        setSelectedSparkCodes: (sparkCodes: TikTokSparkAd[]) => { set(state => { state.selectedSparkCodes = sparkCodes }) },
        setPlaybookSteps: (steps: PlaybookStep[]) => {
            set(state => {
                state.playbookStepNames = steps.map(step => step.name)
            })
        },
        setIsLoadingMaestroRecommendations: (isLoading: boolean) => { set(state => { state.isLoadingMaestroRecommendations = isLoading }) },
        addSparkCode: (sparkCode: TikTokSparkAd) => {
            const {
                previewedAsset,
                setPreviewedAsset,
                selectedSparkCodes
            } = get();

            // Check if this spark code already exists in the selectedSparkCodes array
            const sparkId = sparkCode.item_info?.item_id || sparkCode.video_info?.video_id;
            const isDuplicate = selectedSparkCodes.some(code => {
                const existingId = code.item_info?.item_id || code.video_info?.video_id;
                return existingId === sparkId;
            });

            // If it's a duplicate, return false to indicate it wasn't added
            if (isDuplicate) {
                return false;
            }

            let updatePostWithPlatformType: TikTokSparkAd = {
                ...sparkCode,
                platformType: AssetPlatformType.TIKTOK,
            }

            if (!previewedAsset) {
                let transformedSparkCode = transformPostsToCreativeFormat([updatePostWithPlatformType])[0];
                setPreviewedAsset(transformedSparkCode);
            }

            set(state => {
                state.selectedSparkCodes = [...state.selectedSparkCodes, updatePostWithPlatformType];
            });

            // Return true to indicate the spark code was added
            return true;
        },
        removeSparkCode: (sparkCodeId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            if (previewedAsset?.id === sparkCodeId) {
                setPreviewedAsset(null);
            }
            set(state => {
                state.selectedSparkCodes = state.selectedSparkCodes.filter(code => code.item_info?.item_id !== sparkCodeId);
            });
        },
        getCampaign: async (campaignId: number) => {
            const {
                campaign: currentCampaign,
                validateGeographyBuckets,
                initializeCampaignState
            } = get();

            const {
                loggedInFacebookUser,
                selectedFacebookAdAccount,
                selectedFacebookPage
            } = useFacebookConnections.getState()

            if (!campaignId) {
                campaignId = currentCampaign?.id ?? 0;
            }

            if (campaignId === 0) {
                return null;
            }

            set(state => { state.isLoadingCampaign = true });
            try {
                const campaignLocal: CampaignDetails | null = await getCampaign_api(campaignId);
                if (!campaignLocal) {
                    throw new Error('useCampaign::getCampaign::Campaign not found');
                }

                const playbookTypeLocal = campaignLocal.campaign_metadata?.playbook;
                const adPlatformLocal = PLAYBOOK_TYPE_INFO[playbookTypeLocal].adPlatform;

                // Initialize the campaign state with defaults before applying the stored campaign input
                initializeCampaignState({
                    playbookType: playbookTypeLocal
                });

                const campaignMetadata = campaignLocal?.campaign_metadata;
                let v2Input: FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | null = null;

                set(state => { state.adPlatform = adPlatformLocal });
                switch (adPlatformLocal) {
                    case AdPlatform.META:
                        v2Input = campaignMetadata?.v2_campaign_input as FacebookCampaignSubmissionType;
                        break;
                    case AdPlatform.TIKTOK:
                        v2Input = campaignMetadata?.v2_campaign_input as TikTokCampaignSubmissionType;
                        break;
                    // case AD_PLATFORM.YOUTUBE: // NOTE CONNER: this is not used yet, but will be used in the future
                    //     v2Input = campaignMetadata?.v2_campaign_input as YouTubeCampaignSubmissionType;
                    //     break;
                    default:
                        throw new Error(`useCampaign::getCampaign::Unsupported ad platform: ${adPlatformLocal}`);
                }

                if (v2Input) {
                    set(state => {
                        // TypeScript doesn't know v2Input is non-null here, so we need to assert it
                        const v2InputExists = v2Input!;

                        // Common state updates for specific playbook types
                        switch (adPlatformLocal) {
                            case AdPlatform.TIKTOK:
                                const tiktokInput = v2InputExists as TikTokCampaignSubmissionType;
                                state.enabledPlatforms = tiktokInput.enabledPlatforms ?? [];
                                state.selectedSparkCodes = tiktokInput.selectedSparkCodes || [];
                                break;
                            case AdPlatform.META:
                                const fbInput = v2InputExists as FacebookCampaignSubmissionType;
                                state.enabledPlatforms = fbInput.enabledPlatforms ?? [];
                                state.conversionsEnabled = fbInput.conversionsEnabled;
                                state.conversionsEventName = fbInput.conversionsEventName;
                                break;
                        }

                        // Common state updates for all playbook types
                        state.campaign = campaignLocal
                        state.playbookType = v2InputExists.playbookType;
                        state.budget = String(v2InputExists.budget);
                        state.selectedSongToPromote = v2InputExists.record;

                        let shouldBeExpress = v2InputExists.usesExpressSetup;
                        if (loggedInFacebookUser && selectedFacebookAdAccount && selectedFacebookPage) {
                            shouldBeExpress = false;
                        }

                        state.callToAction = v2InputExists.cta;
                        state.currentStep = PlaybookStepName.CREATIVE;

                        // Assets and content
                        if (v2InputExists.assets) {
                            state.uploadedAssets = v2InputExists.assets
                                .filter((asset: CreativeSelectorPost) => asset.originalPostType === AssetPlatformType.CLOUDINARY)
                                .map((asset: CreativeSelectorPost) => ({
                                    id: asset.id,
                                    batchId: '',
                                    asset_id: asset.id,
                                    public_id: asset.id,
                                    version: 1,
                                    version_id: '1',
                                    signature: '',
                                    width: 0,
                                    height: 0,
                                    format: 'mp4',
                                    resource_type: 'video',
                                    created_at: asset.timestamp || '',
                                    bytes: 0,
                                    type: 'upload',
                                    etag: '',
                                    placeholder: false,
                                    url: asset.assetUrl ?? '',
                                    secure_url: asset.assetUrl ?? '',
                                    access_mode: 'public',
                                    original_filename: asset.title,
                                    path: asset.assetUrl ?? '',
                                    thumbnail_url: asset.thumbnailUrl ?? '',
                                    duration: 0,
                                    tags: [],
                                    folder: '',
                                    existing: false,
                                    platformType: AssetPlatformType.CLOUDINARY
                                }));

                            state.selectedInstagramPosts = v2InputExists.assets
                                .filter((asset: CreativeSelectorPost) => asset.originalPostType === AssetPlatformType.INSTAGRAM)
                                .map((asset: CreativeSelectorPost) => ({
                                    id: asset.id,
                                    ig_id: asset.id,
                                    permalink: asset.assetUrl ?? '',
                                    caption: asset.platformCaption || '',
                                    media_product_type: MediaProductType.FEED,
                                    media_type: MediaType.VIDEO,
                                    media_url: asset.assetUrl,
                                    thumbnail_url: asset.thumbnailUrl || '',
                                    timestamp: asset.timestamp || '',
                                    like_count: asset.likesCount || 0,
                                    comments_count: asset.commentsCount || 0,
                                    selectable: true,
                                    platformType: AssetPlatformType.INSTAGRAM
                                }));
                        }

                        state.linksToPromote = v2InputExists.linksToPromote;
                        state.captions = v2InputExists.captions;
                        state.campaignGoal = v2InputExists.campaignGoal || null;
                        // Targeting
                        state.geographyBuckets = v2InputExists.targetedGeographies || [];
                        state.selectedAudienceGroups = Array.isArray(v2InputExists.targetedAudiences)
                            ? v2InputExists.targetedAudiences.map((audience: TargetedAudience) => ({
                                id: audience.id,
                                title: audience.name,
                                tags: Array.isArray(audience.audiences)
                                    ? audience.audiences.map(a => ({
                                        id: Number.isNaN(Number(a.id)) ? 0 : Number(a.id),
                                        title: a.name || '',
                                        type: a.type as "customAudience" | "lookalikeAudience" | "interestTargetingAudience" | "pageAudience" | "presetAudience"
                                    }))
                                    : []
                            }))
                            : [];

                        // Also convert incoming targetedGeographies to geographyBuckets format
                        // This ensures we maintain the proper structure when loading from draft
                        if (Array.isArray(v2InputExists.targetedGeographies) && v2InputExists.targetedGeographies.length > 0) {
                            state.geographyBuckets = v2InputExists.targetedGeographies.map((geo: TargetedGeographyBucket) => ({
                                id: geo.id,
                                name: geo.name,
                                percentage: geo.percentage,
                                geographies: geo.geographies,
                                excludedGeographies: geo.excludedGeographies
                            })) as TargetedGeographyBucket[];
                        }

                        // Demographics
                        const demographics = v2InputExists.targetedDemographics || {};
                        state.minAge = demographics.min_age || 18;
                        state.maxAge = demographics.max_age || 65;
                        state.genders = Array.isArray(demographics.genders)
                            ? demographics.genders.map((g: TargetedGender) => ({
                                key: g.key,
                                name: g.name
                            }))
                            : [];
                        state.languages = Array.isArray(demographics.languages)
                            ? demographics.languages.map((lang: TargetedLanguage) => {
                                if (typeof lang === 'string') {
                                    return {
                                        key: lang,
                                        name: lang,
                                        selected: true
                                    }
                                }
                                return {
                                    key: lang.key,
                                    name: lang.name,
                                    selected: true
                                }
                            })
                            : [];

                        // Budget and dates configuration
                        state.currentBudgetTab = v2InputExists.budgetType || 'lifetime';
                        state.highlightedTrack = v2InputExists.highlightedTrack || null;

                        if (v2InputExists.lengthConfig) {
                            if (v2InputExists.lengthConfig.dateType === 'date_range') {
                                state.currentDateSelectionTab = 'dateRange';

                                const today = dayjs();

                                // Default fallback dates
                                let newStartDate = today;
                                let newEndDate = today.add(7, 'days');

                                // Check if we have valid start and end dates
                                const hasValidStartDate = Boolean(v2InputExists.lengthConfig.startDate);
                                const hasValidEndDate = Boolean(v2InputExists.lengthConfig.endDate);

                                if (hasValidStartDate && hasValidEndDate) {
                                    const inputStartDate = dayjs(v2InputExists.lengthConfig.startDate);
                                    const inputEndDate = dayjs(v2InputExists.lengthConfig.endDate);

                                    // Check if it's a valid time range (start is before end)
                                    if (inputStartDate.isBefore(inputEndDate)) {
                                        // Calculate the original duration
                                        const originalDuration = inputEndDate.diff(inputStartDate);

                                        // Set end date to today + original duration
                                        newEndDate = today.add(originalDuration, 'millisecond');
                                    }
                                }

                                state.startDate = newStartDate.format('YYYY-MM-DD');
                                state.endDate = newEndDate.format('YYYY-MM-DD');
                            } else if (v2InputExists.lengthConfig.dateType === 'duration') {
                                state.currentDateSelectionTab = 'numberOfDays';

                                // For duration type, check if start date is in the past
                                const inputStartDate = v2InputExists.lengthConfig.startDate ? dayjs(v2InputExists.lengthConfig.startDate) : dayjs();
                                const today = dayjs();

                                // If start date is in the past, reset to today
                                state.startDate = inputStartDate.isBefore(today) ? today.format('YYYY-MM-DD') : inputStartDate.format('YYYY-MM-DD');
                                state.numberOfDays = v2InputExists.lengthConfig?.numberOfDays || 7;
                            } else {
                                // Default to dateRange if dateType is not recognized
                                state.currentDateSelectionTab = 'dateRange';
                                state.startDate = dayjs().format('YYYY-MM-DD');
                                state.endDate = dayjs().add(7, 'days').format('YYYY-MM-DD');
                            }
                        } else {
                            // Default values if lengthConfig is missing
                            state.currentDateSelectionTab = 'dateRange';
                            state.startDate = dayjs().format('YYYY-MM-DD');
                            state.endDate = dayjs().add(7, 'days').format('YYYY-MM-DD');
                        }
                    });
                }

                validateGeographyBuckets();

                return campaignLocal;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isLoadingCampaign = false });
            }
        },

        createCampaign: async (brandId: string) => {
            const {
                budget,
                startDate,
                endDate,
                playbookType,
            } = get();

            const newCampaignData = {
                budget,
                startDate,
                endDate,
                playbookType,
            };

            try {
                // TODO: make a service for this function
                set(state => { state.isLoadingCampaign = true });
                const res = await Axios.post(`/campaign/brand/${brandId}/create`, newCampaignData);

                const campaignLocal: CampaignDetails = res.data.data;

                set(state => { state.campaign = campaignLocal });

                return campaignLocal;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isLoadingCampaign = false });
            }
        },

        setGeographyBuckets: (geographyBuckets: TargetedGeographyBucket[]) => { set(state => { state.geographyBuckets = geographyBuckets }) },
        setShowEditGeographyBucketsBanner: (show: boolean) => { set(state => { state.showEditGeographyBucketsBanner = show }) },
        cleanupEmptyGeographyBuckets: () => {
            const { geographyBuckets } = get();

            // Filter out buckets with no locations
            const bucketsWithLocations = geographyBuckets.filter(bucket => bucket.geographies.length > 0);

            // If no buckets were removed, no cleanup needed
            if (bucketsWithLocations.length === geographyBuckets.length || bucketsWithLocations.length === 0) {
                return;
            }

            // Calculate total percentage from remaining buckets
            const totalRemainingPercentage = bucketsWithLocations.reduce((sum, bucket) => sum + bucket.percentage, 0);

            // Rebalance the remaining buckets to total 100%
            const rebalancedBuckets = bucketsWithLocations.map(bucket => ({
                ...bucket,
                percentage: Math.round((bucket.percentage / totalRemainingPercentage) * 100)
            }));

            // Ensure total equals exactly 100% by adjusting the first bucket if needed
            const currentTotal = rebalancedBuckets.reduce((sum, bucket) => sum + bucket.percentage, 0);
            if (currentTotal !== 100 && rebalancedBuckets.length > 0) {
                rebalancedBuckets[0].percentage += (100 - currentTotal);
            }

            // Update state with cleaned and rebalanced buckets
            set(state => { state.geographyBuckets = rebalancedBuckets });
        },
        validateGeographyBuckets: () => {
            /** Calculates if budget percentages sum to 100% and if all percentages are greater than 0 */
            const { geographyBuckets } = get();
            const total = geographyBuckets.reduce((sum, bucket) => sum + bucket.percentage, 0);
            const isEveryPercentageValid = geographyBuckets.every(bucket => bucket.percentage > 0);
            const everyBucketHasGeographies = geographyBuckets.every(bucket => bucket.geographies.length > 0);
            set(state => { state.isGeographyBucketsObjectValid = total === 100 && isEveryPercentageValid && everyBucketHasGeographies });
        },
        searchGeographies: async (query: string, platform: 'tiktok' | 'facebook'): Promise<TikTokTargetingGeography[] | FBLocation[]> => {
            if (query.length < 2) return [];

            set(state => { state.isLoadingGeographies = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState
            const {
                loggedInFacebookUser
            } = useFacebookConnections.getState()
            try {
                if (platform === 'tiktok') {
                    const locations = await getAvailableTargetingLocations_api(currentBrand.id);
                    if (!locations) return [];

                    // Filter locations based on search query
                    const filteredLocations = locations.filter(location =>
                        location.name.toLowerCase().includes(query.toLowerCase())
                    );

                    return filteredLocations;
                } else {
                    const fbLocations = await searchFacebookGeographies(query, currentBrand.id)
                    return fbLocations;
                }
            } catch (error) {
                console.error('Error fetching geographies:', error);
                return [];
            } finally {
                set(state => { state.isLoadingGeographies = false });
            }
        },

        setMinAge: (age: number) => {
            set(state => { state.minAge = age });
        },

        setMaxAge: (age: number) => {
            set(state => { state.maxAge = age });
        },

        setAgeRange: (minAge: number, maxAge: number) => {
            set(state => {
                state.minAge = minAge;
                state.maxAge = maxAge;
            });
        },

        setTargetedGender: (gender: TargetedGender[]) => {
            set(state => { state.genders = gender });
        },

        setLanguages: (locales: TargetedLanguage[]) => {
            set(state => { state.languages = locales });
        },

        setIsDemographicsAutomated: (isAutomated: boolean) => {
            set(state => { state.isDemographicsAutomated = isAutomated });
        },

        setDemographics: (demographics) => {
            set(state => {
                if (demographics.minAge !== undefined) state.minAge = demographics.minAge;
                if (demographics.maxAge !== undefined) state.maxAge = demographics.maxAge;
                if (demographics.genders !== undefined) state.genders = demographics.genders;
                if (demographics.locales !== undefined) state.languages = demographics.locales;
                if (demographics.isAutomated !== undefined) state.isDemographicsAutomated = demographics.isAutomated;
            });
        },

        setCurrentBudgetTab: (tab: CurrentBudgetTabType) => {
            set(state => { state.currentBudgetTab = tab });
        },

        setCurrentDateSelectionTab: (tab: CurrentDateSelectionTabType) => {
            set(state => { state.currentDateSelectionTab = tab });
        },

        setHighlightedTrack: (track: {
            id: string;
            uri: string;
            name: string;
        }) => {
            set(state => { state.highlightedTrack = track });
        },

        setSelectedSongToPromote: (songToPromote: RecordDetails) => {
            let external_links = songToPromote?.external_links
            set(state => {
                state.selectedSongToPromote = songToPromote
                state.linksToPromote = []
                state.enabledPlatforms = external_links?.map(link => ({
                    url: link.url,
                    name: link.name
                })) || []
            });
        },

        getDataForDebugBox: () => {
            const {
                budget,
                startDate,
                endDate,
                campaignGoal: goal,
                currentStep,
                callToAction,
                selectedSparkCodes,
                selectedSongToPromote,
                playbookStepNames,
                linksToPromote,
                enabledPlatforms,
                minAge,
                maxAge,
                genders: gender,
                languages,
                uploadedAssets,
                captions,
                selectedAudienceGroups,
                isDemographicsAutomated,
                geographyBuckets
            } = get();

            return {
                budget,
                startDate,
                endDate,
                goal,
                currentStep,
                callToAction,
                playbookStepNames,
                linksToPromote,
                enabledPlatforms,
                minAge,
                maxAge,
                gender,
                languages,
                selectedSongToPromote,
                uploadedAssets,
                captions,
                selectedAudienceGroups,
                isDemographicsAutomated,
                geographyBuckets
            }
        },

        nextCampaignStep: () => {
            const {
                currentStep,
                playbookStepNames,
                adPlatform,
                playbookType
            } = get();

            const {
                currentBrand
            } = useBrand.getState() as BrandExistsState;

            if (!currentStep) {
                set(state => { state.currentStep = playbookStepNames[0] });
                return;
            } else {
                const currentStepIndex = playbookStepNames.findIndex(stepName => stepName === currentStep);
                const currentStepName = playbookStepNames[currentStepIndex];

                // Get the next step
                const nextStep = playbookStepNames[currentStepIndex + 1];

                // Check if the next step is the finalization step by name
                const isMovingToFinalization = nextStep && nextStep.includes('FINALIZATION');

                if (!nextStep) {
                    return;
                }

                let analyticsEventTitle = ''
                switch (currentStepName) {
                    case PlaybookStepName.GOAL_SELECTOR:
                    case PlaybookStepName.RECORD_SELECTOR:
                    case PlaybookStepName.LINK_SELECTOR:
                        analyticsEventTitle = 'Campaign Content'
                        break;
                    default:
                        analyticsEventTitle = currentStepName
                        break;
                }

                track(`${analyticsEventTitle} Completed`, {
                    adPlatform,
                    playbookType,
                    brand_id: currentBrand.id,
                    brand_slug: currentBrand.slug,
                    brand_name: currentBrand.name,
                })

                set(state => { state.currentStep = nextStep });

                // If we're moving to the finalization step, scroll to the checkout buttons
                if (isMovingToFinalization) {
                    // Increase timeout to ensure the DOM has fully updated
                    setTimeout(() => {

                        // Try using the global function we added to the component
                        // @ts-ignore
                        if (window.scrollToSubmitButtons && typeof window.scrollToSubmitButtons === 'function') {
                            // @ts-ignore
                            window.scrollToSubmitButtons();
                            return;
                        }

                        // Try the ID first
                        const submitButtonsContainer = document.getElementById('campaignSubmitButtonsContainer');

                        if (submitButtonsContainer) {
                            submitButtonsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                            // Try various fallback selectors
                            const fallbackSelectors = [
                                '.submitCampaignOrUpgradeComponent',
                                '.checkoutWithCardButton',
                                '.submitCampaignButton',
                                '.unlockUnlimitedButton',
                                '.campaignSubmitButtonsContainer',
                                '.submitOrCheckoutButtonsView'
                            ];

                            for (const selector of fallbackSelectors) {
                                const element = document.querySelector(selector);
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    break;
                                }
                            }
                        }
                    }, 500); // Increased timeout to 500ms to ensure DOM is updated
                }
            }
        },

        prevCampaignStep: () => {
            const {
                currentStep,
                playbookStepNames
            } = get();

            if (!currentStep) {
                set(state => { state.currentStep = playbookStepNames[0] });
                return;
            } else {
                const prevStep = playbookStepNames[playbookStepNames.findIndex(stepName => stepName === currentStep) - 1];
                if (prevStep) {
                    set(state => { state.currentStep = prevStep });
                }
            }
        },

        resetDemographicsToRecommended: () => {
            set(state => {
                state.minAge = 18;
                state.maxAge = 35;
                state.genders = FB_DEFAULT_GENDERS;
                state.languages = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
            })
        },

        resetAudiencesToRecommended: () => {
            const { preserveLoadedAudienceGroups } = get();
            set(state => {
                state.selectedAudienceGroups = preserveLoadedAudienceGroups(false);
            })
        },

        resetGeographyBuckets: (multipleLocations?: boolean) => {
            const {
                playbookType,
                adPlatform
            } = get();

            let defaultGeographyBuckets: TargetedGeographyBucket[] = [];

            // First determine the default buckets based on playbook type
            switch (playbookType) {
                case PlaybookType.INCREASE_MUSIC_STREAMS:
                case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                case PlaybookType.META_PRESAVES:
                    defaultGeographyBuckets = [
                        {
                            id: 0,
                            name: 'Touring Markets',
                            percentage: 70,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        },
                        {
                            id: 1,
                            name: 'Global Markets',
                            percentage: 30,
                            geographies: DEFAULT_GLOBAL_MARKET_GEOGRAPHIES
                        }
                    ];
                    break;

                case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                case PlaybookType.TIKTOK_EXTERNAL_TRAFFIC:
                case PlaybookType.TIKTOK_SOUND:
                case PlaybookType.TIKTOK_MUSIC_STREAMS:
                    defaultGeographyBuckets = DEFAULT_TIKTOK_GEOGRAPHIES.map(geography => ({
                        id: geography.id,
                        name: geography.name,
                        percentage: 100,
                        geographies: geography.geographies,
                    }));
                    break;

                case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                case PlaybookType.INCREASE_LINK_CLICKS:
                case PlaybookType.META_TICKET_SALES:
                case PlaybookType.META_EMAIL_SIGNUPS:
                case PlaybookType.META_IG_SOUND:
                case PlaybookType.INCREASE_VIDEO_VIEWS:
                    defaultGeographyBuckets = [{
                        id: 0,
                        name: 'Primary Markets',
                        percentage: 100,
                        geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                    }];
                    break;

                default:
                    // Default to single primary market bucket for any new playbook types
                    defaultGeographyBuckets = [{
                        id: 0,
                        name: 'Primary Markets',
                        percentage: 100,
                        geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                    }];
            }

            // If multipleLocations is false, always reduce to a single bucket
            if (!multipleLocations) {
                // Keep only the first bucket and ensure it has 100% allocation
                defaultGeographyBuckets = [defaultGeographyBuckets[0]];
                defaultGeographyBuckets[0].percentage = 100;
            }

            set(state => {
                state.geographyBuckets = defaultGeographyBuckets;
                // Reset the edit banner when geography buckets are reset
                state.showEditGeographyBucketsBanner = false;
            });
        },

        /**
         * Preserves existing audience groups when initializing campaign state
         * @param preserveExisting - Whether to preserve existing audience groups
         * @returns The audience groups to use (preserved or default)
         */
        preserveLoadedAudienceGroups: (preserveExisting: boolean = true): TargetingAudienceGroup[] => {
            const { selectedAudienceGroups, adPlatform, conversionsEnabled, usesExpressSetup, playbookType } = get();

            const selectedAudienceGroupsLocal = selectedAudienceGroups || [] // NOTE CONNER: most likely dont need this because selectedAudienceGroups is set in the initializeCampaignState function

            // If there are existing audience groups and we should preserve them, return the existing ones
            if (preserveExisting && selectedAudienceGroupsLocal.length > 0) {
                return selectedAudienceGroupsLocal;
            }

            // Otherwise return default audience groups
            const isTikTok = adPlatform === AdPlatform.TIKTOK;
            const defaultAudienceGroups: TargetingAudienceGroup[] = [];

            if (!isTikTok) {
                defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS);
                defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.CURRENT_FANS);
            } else {
                defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.TIKTOK_RECOMMENDED_AUDIENCES);
            }

            // Add conversion event lookalike audience based on playbook configuration and setup type
            const playbookInfo = playbookType ? PLAYBOOK_TYPE_INFO[playbookType] : null;
            const playbookSupportsConversions = playbookInfo?.hasConversions === true;


            if (!isTikTok && playbookSupportsConversions) {
                const { selectedFacebookPixel } = useFacebookConnections.getState();
                const isExpressFlow = usesExpressSetup();

                if (isExpressFlow) {
                    // Express flow: Always include conversion lookalikes (Symphony handles pixel)
                    defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES);
                } else {
                    // Custom setup: Only include if user has connected their own pixel and enabled conversions
                    if (conversionsEnabled && selectedFacebookPixel?.id) {
                        defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES);
                    }
                }
            }

            return defaultAudienceGroups;
        },

        initializeCampaignState: ({
            playbookType,
            currentStep,
            playbookStepNames,
        }: {
            playbookType: PlaybookType;
            playbookStepNames?: PlaybookStepName[];
            currentStep?: PlaybookStepName;
        }) => {
            const {
                preserveLoadedAudienceGroups,
                campaignInitialized,
                usesExpressSetup
            } = get();

            const {
                selectedFacebookPixel
            } = useFacebookConnections.getState();

            const { isProBrand } = useBrand.getState();

            try {
                if (!playbookType) {
                    throw new Error('Playbook type is required');
                }

                const adPlatform = PLAYBOOK_TYPE_INFO[playbookType].adPlatform;
                if (!adPlatform) {
                    throw new Error(`Playbook type ${playbookType} doesn't have an associated ad platform`);
                }

                if (playbookStepNames) {
                    set(state => {
                        state.playbookStepNames = playbookStepNames;
                        state.currentStep = currentStep || playbookStepNames[0];
                    });
                }

                set(state => {
                    state.playbookType = playbookType;
                    state.adPlatform = adPlatform;
                });

                if (campaignInitialized) {
                    return;
                }

                // Determine if user should be restricted to single location
                const isExpressFlow = usesExpressSetup();
                const shouldRestrictToSingleLocation = isExpressFlow || !isProBrand;

                // set default values for campaigns here
                let callToActionDefault: CallToActionType;
                let conversionsEventNameDefault: string;
                let conversionsEnabledDefault: boolean;
                let campaignDisclaimerDefault: string;
                let linksToPromoteDefault: ExternalLink[] = [];
                let ageMaxDefault = FB_DEFAULT_MAX_AGE;
                let ageMinDefault = FB_DEFAULT_MIN_AGE;

                let gendersDefault: TargetedGender[] = FB_DEFAULT_GENDERS;
                let languagesDefault: TargetedLanguage[] = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                let geographyBucketsDefault: TargetedGeographyBucket[] = []

                let budgetDefault = '100';
                let startDateDefault = dayjs();
                let endDateDefault = dayjs(startDateDefault).add(7, 'days').toDate();
                let budgetTypeDefault = CURRENT_BUDGET_TABS.LIFETIME
                let audienceGroupsDefault: TargetingAudienceGroup[] = []

                switch (playbookType) {
                    // Meta/Facebook Playbooks
                    case PlaybookType.INCREASE_MUSIC_STREAMS:
                    case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                        audienceGroupsDefault = preserveLoadedAudienceGroups(false);
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.'
                        languagesDefault = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                        gendersDefault = FB_DEFAULT_GENDERS;
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        callToActionDefault = { value: 'LISTEN_NOW', label: 'Listen Now' };

                        // Respect user restrictions for geography buckets
                        if (shouldRestrictToSingleLocation) {
                            geographyBucketsDefault = [{
                                id: 0,
                                name: 'Primary Markets',
                                percentage: 100,
                                geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                            }];
                        } else {
                            geographyBucketsDefault = [
                                {
                                    id: 0,
                                    name: 'Touring Markets',
                                    percentage: 70,
                                    geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                                },
                                {
                                    id: 1,
                                    name: 'Global Markets',
                                    percentage: 30,
                                    geographies: DEFAULT_GLOBAL_MARKET_GEOGRAPHIES
                                }
                            ];
                        }

                        // Don't enable conversions during initialization - let the ConnectionsCampaignView 
                        // useEffect handle this reactively based on actual pixel connection status
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        break;

                    case PlaybookType.META_PRESAVES:
                        // Don't enable conversions during initialization - let the ConnectionsCampaignView 
                        // useEffect handle this reactively based on actual pixel connection status
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };

                        // Respect user restrictions for geography buckets  
                        if (shouldRestrictToSingleLocation) {
                            geographyBucketsDefault = [{
                                id: 0,
                                name: 'Primary Markets',
                                percentage: 100,
                                geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                            }];
                        } else {
                            geographyBucketsDefault = [
                                {
                                    id: 0,
                                    name: 'Touring Markets',
                                    percentage: 70,
                                    geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                                },
                                {
                                    id: 1,
                                    name: 'Global Markets',
                                    percentage: 30,
                                    geographies: DEFAULT_GLOBAL_MARKET_GEOGRAPHIES
                                }
                            ];
                        }

                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.';
                        // Use preserveLoadedAudienceGroups to ensure proper Express flow handling
                        audienceGroupsDefault = preserveLoadedAudienceGroups(false);
                        break;

                    case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                    case PlaybookType.INCREASE_LINK_CLICKS:
                    case PlaybookType.META_TICKET_SALES:
                    case PlaybookType.META_EMAIL_SIGNUPS:
                    case PlaybookType.META_IG_SOUND:
                        // These playbooks always use single location bucket
                        audienceGroupsDefault = preserveLoadedAudienceGroups(false);
                        languagesDefault = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                        gendersDefault = FB_DEFAULT_GENDERS;
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.'

                        if (playbookType === PlaybookType.INCREASE_LINK_CLICKS ||
                            playbookType === PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT) {
                            linksToPromoteDefault = [{ url: '', name: 'Main Link' }];
                        }

                        switch (playbookType) {
                            case PlaybookType.META_EMAIL_SIGNUPS:
                                callToActionDefault = { value: 'SIGN_UP', label: 'Sign Up' };
                                break;
                            case PlaybookType.META_IG_SOUND:
                                callToActionDefault = { value: 'WATCH_MORE', label: 'Watch More' };
                                break;
                            default:
                                callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        }
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];

                        // Don't enable conversions during initialization - let the ConnectionsCampaignView 
                        // useEffect handle this reactively based on actual pixel connection status
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        break;

                    // TikTok Playbooks - always single location
                    case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                    case PlaybookType.TIKTOK_EXTERNAL_TRAFFIC:
                    case PlaybookType.TIKTOK_SOUND:
                    case PlaybookType.TIKTOK_MUSIC_STREAMS:
                        geographyBucketsDefault = DEFAULT_TIKTOK_GEOGRAPHIES;
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        campaignDisclaimerDefault = 'Your marketing campaign may take up to 48 hours to go live. You will be redirected to a payment page to finish checkout.';
                        audienceGroupsDefault = [
                            SHARED_AUDIENCE_CONFIGS.TIKTOK_RECOMMENDED_AUDIENCES,
                        ];
                        // TikTok doesn't use Facebook conversions
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        break;

                    // YouTube Playbooks - always single location
                    case PlaybookType.INCREASE_VIDEO_VIEWS:
                        campaignDisclaimerDefault = 'Your marketing campaign may take up to 48 hours to go live. You will be redirected to a payment page to finish checkout.';
                        callToActionDefault = { value: 'WATCH_NOW', label: 'Watch Now' };
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        // YouTube doesn't use Facebook conversions
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        startDateDefault = dayjs().add(1, 'day')
                        endDateDefault = dayjs().add(4, 'days').toDate();
                        break;

                    // Playlisting Playbooks
                    case PlaybookType.GET_PLAYLISTED:
                        campaignDisclaimerDefault = 'Your campaign will be reviewed by our playlist pitching team for eligibility. Once they\'ve reviewed, it may take up to 7 days to go live as they find relevant playlists. If we are not able to pitch your song effectively, you will be refunded the full amount of your payment.';
                        callToActionDefault = { value: 'LISTEN_NOW', label: 'Listen Now' };
                        // Playlisting doesn't use Facebook conversions
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                        break;
                    // Default case for any new playbook types
                    default:
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        campaignDisclaimerDefault = 'Your campaign will be submitted for review. It may take up to 24 hours to go live.';
                        // Default to no conversions - let ConnectionsCampaignView handle this reactively
                        conversionsEnabledDefault = false;
                        conversionsEventNameDefault = '';
                }

                set(state => {
                    // If we're not preserving data, set all defaults
                    state.adPlatform = adPlatform;
                    state.minAge = ageMinDefault;
                    state.maxAge = ageMaxDefault;
                    state.callToAction = callToActionDefault;
                    state.linksToPromote = linksToPromoteDefault;
                    state.selectedAudienceGroups = audienceGroupsDefault;
                    state.campaignDisclaimer = campaignDisclaimerDefault;
                    state.geographyBuckets = geographyBucketsDefault;
                    state.languages = languagesDefault;
                    state.genders = gendersDefault;
                    state.budget = budgetDefault;
                    if (adPlatform === AdPlatform.TIKTOK) {
                        state.startDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
                    } else {
                        state.startDate = dayjs(startDateDefault).format('YYYY-MM-DD');
                    }
                    state.endDate = dayjs(endDateDefault).format('YYYY-MM-DD');
                    state.currentBudgetTab = budgetTypeDefault;
                    state.campaignInitialized = true;
                    state.conversionsEnabled = conversionsEnabledDefault;
                    state.conversionsEventName = conversionsEventNameDefault;
                });
            } catch (e) {
                console.error('Error initializing campaign state:', e);
            }
        },

        submitCampaign: async ({
            submitType,
            recurring_interval,
            overrideCampaignInput
        }: {
            submitType: 'directCreate' | 'payment_intent',
            recurring_interval?: BillingCycle,
            overrideCampaignInput?: YouTubeCampaignData | PlaylistingCampaignInput | PlaylistingCampaignSubmissionType
        }) => {
            const {
                playbookType,
                getDataForSubmission
            } = get();

            const { currentBrand } = useBrand.getState() as BrandExistsState;

            try {
                set(state => { state.isSubmittingCampaign = true });
                let res: any = null;

                let campaignInput: PlaylistingCampaignInput
                    | PlaylistingCampaignSubmissionType
                    | TikTokCampaignSubmissionType
                    | YouTubeCampaignData
                    | FacebookCampaignSubmissionType
                    = overrideCampaignInput ?? getDataForSubmission();

                track(`Submit Campaign`, {
                    brand_id: currentBrand.id,
                    brand_slug: currentBrand.slug,
                    brand_name: currentBrand.name,
                    playbook_type: playbookType,
                    submit_type: submitType
                })

                switch (submitType) {
                    case 'directCreate':
                        if (playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
                            res = await createTiktokCampaign_v2_api({
                                brandId: currentBrand.id,
                                campaignInput: campaignInput as TikTokCampaignSubmissionType
                            });
                        } else {
                            res = await createDirectFacebookCampaign_api({
                                brandId: currentBrand.id,
                                campaign_input: campaignInput as FacebookCampaignSubmissionType
                            });
                        }
                        break;
                    case 'payment_intent':
                        res = await createCampaignAfterPaymentIntent_api({
                            brandId: currentBrand.id,
                            campaign_input: campaignInput
                        });
                        break;


                }

                // If response contains a campaign ID, fetch the campaign data
                if ((res as any)?.campaign) {
                    // Set the campaign in the state
                    set(state => { state.campaign = ((res as any).campaign as unknown) as CampaignDetails });
                }

                return res;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isSubmittingCampaign = false });
            }
        },

        submitCampaignFromAdBox: async () => {
            const {
                getDataForSubmission
            } = get();

            try {
                set(state => { state.isSubmittingCampaign = true });

                let adBoxInput = getDataForSubmission() as FacebookCampaignSubmissionType;

                const res = await createAdBoxCampaign_api({
                    campaign_input: adBoxInput
                });

                // Handle Stripe URL redirection
                if (res?.url) {
                    window.location.href = res.url;
                    return { redirected: true };
                }

                return res;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isSubmittingCampaign = false });
            }
        },

        resetCampaignState: () => {
            set(state => {
                // Reset all campaign state properties
                state.budget = '100'  // Default value is 100
                state.budgetMaximum = '100'  // Default value is 100
                state.startDate = dayjs().format('YYYY-MM-DD') // Use consistent date format
                state.endDate = dayjs().add(7, 'days').format('YYYY-MM-DD') // Use consistent date format
                state.playbookType = null
                state.adPlatform = null
                state.campaignGoal = null
                state.campaign = null
                state.showOneClickCampaignModal = false
                state.showOneClickCampaignSetupModal = false
                state.showCreateNewCampaignModal = false
                state.campaignName = ''
                state.isLoadingCampaign = false
                state.isSubmittingCampaign = false
                state.isLoadingSocialItems = false
                state.numberOfDays = 0
                state.selectedInstagramPosts = []
                state.showEditGeographyBucketsBanner = false
                state.captions = []
                state.uploadedAssets = []
                state.enabledPlatforms = []
                state.currentStep = undefined
                state.callToAction = { value: 'LEARN_MORE', label: 'Learn More' }
                state.selectedSparkCodes = []
                state.campaignInitialized = false
                state.geographyBuckets = [{
                    id: 1,
                    name: '',
                    percentage: 100,
                    geographies: []
                }]
                state.isLoadingGeographies = false
                state.selectedAudienceGroups = []
                state.campaignGoal = null
                state.isGeneratingOneClickCampaign = false
                state.minAge = 18
                state.maxAge = 65
                state.genders = []
                state.languages = []
                state.isDemographicsAutomated = true
                state.previewedAsset = null
                state.currentBudgetTab = CURRENT_BUDGET_TABS.LIFETIME // Default is LIFETIME
                state.currentDateSelectionTab = CURRENT_DATE_SELECTION_TABS.DATE_RANGE
                state.playbookStepNames = []
                state.linksToPromote = []
                state.selectedSongToPromote = null
                state.highlightedTrack = null
                state.isLoadingMaestroRecommendations = false
                state.conversionsEventName = undefined
                state.conversionsEnabled = false
                state.importedFromUrlPosts = []

                // Reset the selected posts
                state.selectedInstagramPost = null
                state.selectedRelease = null
                state.selectedTikTokPost = null
                state.selectedYouTubeVideo = null

                // Clear fetched items
                state.instagramPosts = []
                state.releases = []
                state.tikTokPosts = []
                state.youTubeVideos = []

                // Reset disclaimer
                state.campaignDisclaimer = ''

                // Reset automation fee data
                state.automationFeeData = null
                state.isLoadingAutomationFee = false

                // Reset confirmation checkbox
                state.campaignConfirmationChecked = false
                
                // Reset playlisting-specific fields
                state.pitchNotes = ''
                state.selectedPlaylistingTier = null
            })
        },

        // for displaying the posts
        /** Get all selected posts across different types */
        getAllSelectedPosts: (variant: 'facebook' | 'tiktok'): CreativeSelectorPost[] => {
            const {
                selectedInstagramPosts = [],
                uploadedAssets = [],
                selectedSparkCodes = [],
                importedFromUrlPosts = []
            } = get();

            const transformedIGPosts = transformPostsToCreativeFormat(selectedInstagramPosts);
            const transformedUploadedAssets = transformPostsToCreativeFormat(uploadedAssets);
            const transformedSparkPosts = transformPostsToCreativeFormat(selectedSparkCodes);
            const transformedImportedFromUrlPosts = transformPostsToCreativeFormat(importedFromUrlPosts);

            return variant === 'facebook' ?
                [...transformedIGPosts, ...transformedUploadedAssets, ...transformedImportedFromUrlPosts] :
                [...transformedSparkPosts, ...transformedImportedFromUrlPosts];
        },

        getInstagramPosts: async (): Promise<InstagramPost[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getInstagramPosts_api();
                const response = await fakeAsync(1000);
                set(state => { state.instagramPosts = response as InstagramPost[] });
                return response as InstagramPost[];
            } catch (error) {
                console.error('Error fetching Instagram posts:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getReleases: async (): Promise<RecordDetails[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getReleases_api();
                const response = await fakeAsync(1000);
                set(state => { state.releases = response as RecordDetails[] });
                return response as RecordDetails[];
            } catch (error) {
                console.error('Error fetching releases:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getTikTokPosts: async (): Promise<any[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getTikTokPosts_api();
                const response = await fakeAsync(1000);
                set(state => { state.tikTokPosts = response as any[] });
                return response as any[];
            } catch (error) {
                console.error('Error fetching TikTok posts:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getYouTubeVideos: async (): Promise<any[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getYouTubeVideos_api();
                const response = await fakeAsync(1000);
                set(state => { state.youTubeVideos = response as any[] });
                return response as any[];
            } catch (error) {
                console.error('Error fetching YouTube videos:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        setSelectedInstagramPost: (post: InstagramPost | null) => {
            set(state => { state.selectedInstagramPost = post });
        },

        setSelectedRelease: (release: RecordDetails | null) => {
            set(state => { state.selectedRelease = release });
        },

        setSelectedTikTokPost: (post: any | null) => {
            set(state => { state.selectedTikTokPost = post });
        },

        setSelectedYouTubeVideo: (video: any | null) => {
            set(state => { state.selectedYouTubeVideo = video });
        },

        generateOneClickCampaign: async () => {
            try {
                set(state => { state.isGeneratingOneClickCampaign = true });

                const {
                    getDataForSubmission
                } = get();

                const { currentBrand } = useBrand.getState() as BrandExistsState

                const campaignInput = getDataForSubmission();

                const response = await getOneClickCampaignDetails_api({
                    brandId: currentBrand?.id,
                    campaignInput: campaignInput as FacebookCampaignSubmissionType
                });
            } catch (error) {
                console.error('Error generating one click campaign:', error);
            } finally {
                set(state => { state.isGeneratingOneClickCampaign = false });
            }
        },


        getDataForSubmission: () => {
            const {
                playbookType,
                adPlatform,
                budget,
                startDate,
                endDate,
                selectedSongToPromote,
                callToAction,
                uploadedAssets,
                selectedInstagramPosts,
                captions,
                selectedAudienceGroups,
                minAge,
                maxAge,
                genders: gender,
                linksToPromote,
                highlightedTrack,
                enabledPlatforms,
                languages,
                currentBudgetTab,
                currentDateSelectionTab,
                conversionsEventName,
                conversionsEnabled,
                geographyBuckets,
                campaignGoal,
                selectedSparkCodes,
                pitchNotes,
                importedFromUrlPosts,
                numberOfDays,
                selectedPlaylistingTier
            } = get();

            const {
                selectedFacebookAdAccount,
                selectedFacebookPixel,
                selectedFacebookPage,
                selectedInstagramPage,
            } = useFacebookConnections.getState();

            const {
                tiktokAdAccount
            } = useTikTokConnections.getState();

            // Base submission data matching FacebookCampaignSubmissionType
            const submissionData: Partial<CampaignState> = {
                playbookType,
                selectedPlaylistingTier,
                selectedSongToPromote,
                callToAction,
                uploadedAssets,
                selectedInstagramPosts,
                importedFromUrlPosts,
                linksToPromote,
                enabledPlatforms,
                pitchNotes,
                captions,
                selectedAudienceGroups,
                budget,
                currentBudgetTab,
                currentDateSelectionTab,
                startDate: startDate || '',
                endDate: endDate || '',
                minAge,
                maxAge,
                genders: gender,
                languages,
                conversionsEventName,
                conversionsEnabled,
                highlightedTrack,
                geographyBuckets,
                campaignGoal,
                selectedSparkCodes,
                numberOfDays,
            };

            const transformedCampaignData = transformDataForCampaignSubmit(adPlatform!, submissionData, {
                selectedFacebookAdAccount: selectedFacebookAdAccount || undefined,
                selectedFacebookPixel: selectedFacebookPixel || undefined,
                selectedFacebookPage: selectedFacebookPage || undefined,
                selectedInstagramPage: selectedInstagramPage || undefined,
                selectedTikTokAdAccount: tiktokAdAccount || undefined,
            });
            return transformedCampaignData;
        },

        resetSelectedPosts: () => {
            set(state => {
                state.selectedInstagramPosts = [];
                state.uploadedAssets = [];
                state.selectedSparkCodes = [];
                state.importedFromUrlPosts = [];
                state.previewedAsset = null;
            });
        },

        setCampaignConfirmationChecked: (checked: boolean) => {
            set(state => { state.campaignConfirmationChecked = checked });
        },

        getConfirmationDisclaimerText: () => {
            const { adPlatform, usesExpressSetup } = get();

            switch (adPlatform) {
                case AdPlatform.TIKTOK:
                    return 'Once submitted, this campaign won\'t be able to be edited. If your campaign is denied by TikTok\'s ads review, your ads budget will be refunded.';
                case AdPlatform.YOUTUBE:
                    return 'Once submitted, this campaign won\'t be able to be edited. If your campaign is denied by YouTube\'s ads review, your ads budget will be refunded.';
                case AdPlatform.META:
                    if (usesExpressSetup()) {
                        return 'Once submitted, this campaign won\'t be able to be edited. If your campaign is denied by Meta\'s ads review, your ads budget will be refunded.';
                    }
                    return null;
                default:
                    return null;
            }
        },

        isConfirmationRequired: () => {
            const { adPlatform, usesExpressSetup } = get();

            if (adPlatform === AdPlatform.TIKTOK || adPlatform === AdPlatform.YOUTUBE) {
                return true;
            }

            if (adPlatform === AdPlatform.META && usesExpressSetup()) {
                return true;
            }

            return false;
        },

        /** Get campaign plan details for new users */
        getAvailablePlansforCampaign: async (): Promise<void> => {
            const {
                budget,
                adPlatform,
                usesExpressSetup
            } = get();

            const { currentBrand } = useBrand.getState() as BrandExistsState;
            const { selectedFacebookAdAccount } = useFacebookConnections.getState();

            // Don't calculate plan details if essential data is missing
            if (!budget || !adPlatform || !currentBrand?.id) {
                set(state => {
                    state.campaignPlanDetailsData = null;
                    state.isLoadingCampaignPlanDetails = false;
                });
                return;
            }

            const budgetNumber = Number(budget);
            if (isNaN(budgetNumber) || budgetNumber <= 0) {
                set(state => {
                    state.campaignPlanDetailsData = null;
                    state.isLoadingCampaignPlanDetails = false;
                });
                return;
            }

            // Map AdPlatform to API platform type
            let platformType: 'META' | 'YOUTUBE' | 'TIKTOK' | 'PLAYLISTS';
            switch (adPlatform) {
                case AdPlatform.META:
                    platformType = 'META';
                    break;
                case AdPlatform.TIKTOK:
                    platformType = 'TIKTOK';
                    break;
                case AdPlatform.YOUTUBE:
                    platformType = 'YOUTUBE';
                    break;
                case AdPlatform.PLAYLISTS:
                    platformType = 'PLAYLISTS';
                    break;
                default:
                    // Default to META for unsupported platforms
                    platformType = 'META';
            }

            const currency = selectedFacebookAdAccount?.currency || 'USD';
            const isExpressSetup = usesExpressSetup();

            try {
                set(state => { state.isLoadingCampaignPlanDetails = true });

                const planDetailsData = await getAvailablePlansForCampaignCheckout(currentBrand.id, {
                    adPlatform: platformType,
                    budget: budgetNumber,
                    currency,
                    usesExpressSetup: isExpressSetup
                });

                set(state => { state.campaignPlanDetailsData = planDetailsData });
            } catch (error) {
                console.error('Error getting campaign plan details:', error);
                set(state => { state.campaignPlanDetailsData = null });
            } finally {
                set(state => { state.isLoadingCampaignPlanDetails = false });
            }
        },

        /** Set campaign plan details data */
        setCampaignPlanDetailsData: (data: AvailablePlansForCampaignCheckout | null) => {
            set(state => { state.campaignPlanDetailsData = data });
        },

        /** Set loading state for campaign plan details calculation */
        setIsLoadingCampaignPlanDetails: (loading: boolean) => {
            set(state => { state.isLoadingCampaignPlanDetails = loading });
        },

        // Playlisting-specific setters
        setPitchNotes: (notes: string) => {
            set(state => { state.pitchNotes = notes });
        },

        setSelectedPlaylistingTier: (tier: {
            tier: number;
            cost: number;
            proDiscount: number;
            bgImage: string;
        } | null) => {
            set(state => { 
                state.selectedPlaylistingTier = tier 
                state.budget = String(tier?.cost || 0)
            });
        },

        setCampaignDataFromAgent: (agentCampaign: AgentCampaignData) => {
            const { initializeCampaignState, resetGeographyBuckets, preserveLoadedAudienceGroups } = get();
            const input = agentCampaign.campaign_input;

            // Initialize base state for the playbook type first
            // This sets up some defaults which we might then override
            initializeCampaignState({ playbookType: input.playbookType });

            set(state => {
                state.campaignName = input.campaignName;
                // playbookType and adPlatform are set by initializeCampaignState

                state.budget = String(input.totalAdsBudget || input.budget); // Prefer totalAdsBudget
                state.currentBudgetTab = input.budgetType;

                state.startDate = input.length.startDate;
                state.endDate = input.length.endDate;
                state.currentDateSelectionTab = 'dateRange'; // Agent campaigns provide a fixed range

                // Goal & Links
                // Attempt to find a matching CampaignGoal or set a simple one
                const matchingPlaybookInfo = PLAYBOOK_TYPE_INFO[input.playbookType];
                // goalMatcher property doesn't exist, skip this logic for now
                state.campaignGoal = null;
                if (!state.campaignGoal && matchingPlaybookInfo) {
                    // Create a fallback goal
                    state.campaignGoal = {
                        id: input.playbookType, // Use playbook type as a unique ID for the goal
                        label: matchingPlaybookInfo.title,
                        icon: matchingPlaybookInfo.icon || 'defaultIcon',
                        description: `Promote content via ${matchingPlaybookInfo.title}.`,
                        recommendation: '',
                        conversionGoal: '', // This might need more specific logic
                        tips: '',
                        conversionType: '', // This might need more specific logic
                        averageConversionCost: 0,
                        available: true,
                    };
                }
                state.linksToPromote = [{ url: input.goalLink, name: 'Primary Goal Link' }];
                state.enabledPlatforms = [{ url: input.goalLink, name: 'Website' }]; // Default assumption

                // Creatives
                state.uploadedAssets = [];
                state.selectedInstagramPosts = [];
                state.selectedSparkCodes = [];
                state.importedFromUrlPosts = [];
                state.captions = [];

                input.creatives.forEach((creativeVar, index) => {
                    state.captions.push({
                        id: index + 1,
                        text: creativeVar.caption,
                        aiGenerated: creativeVar.source.wasAiGenerated || false,
                    });

                    const source = creativeVar.source;
                    switch (source.type) {
                        case 'uploaded_asset':
                            state.uploadedAssets.push({
                                id: source.cloudinaryId!,
                                public_id: source.cloudinaryId!,
                                asset_id: source.cloudinaryId!,
                                url: source.mediaUrl,
                                secure_url: source.mediaUrl,
                                thumbnail_url: source.thumbnailUrl,
                                original_filename: `Asset ${index + 1}`,
                                resource_type: source.assetType!, // 'image' | 'video'
                                platformType: AssetPlatformType.CLOUDINARY,
                                batchId: '', version: 1, version_id: '', signature: '',
                                width: 0, height: 0, format: '', created_at: new Date().toISOString(), bytes: 0,
                                type: 'upload', etag: '', placeholder: false, access_mode: 'public',
                                path: source.mediaUrl, tags: [], folder: '', existing: true, // Assuming existing
                            });
                            break;
                        case 'instagram_post':
                            state.selectedInstagramPosts.push({
                                id: source.postId!,
                                ig_id: source.postId!,
                                permalink: source.postUrl || `https://instagram.com/p/${source.postId}`,
                                caption: creativeVar.caption,
                                media_product_type: MediaProductType.FEED, // Assumption
                                media_type: source.mediaUrl.includes('.mp4') ? MediaType.VIDEO : MediaType.IMAGE, // Simple check
                                media_url: source.mediaUrl,
                                thumbnail_url: source.thumbnailUrl,
                                timestamp: new Date().toISOString(), // Placeholder
                                platformType: AssetPlatformType.INSTAGRAM,
                                selectable: true,
                                like_count: 0, // Default value
                                comments_count: 0, // Default value
                            });
                            break;
                        case 'spark_code':
                            const sparkId = source.video_info?.video_id || source.item_info?.item_id || source.sparkCode!;
                            state.selectedSparkCodes.push({
                                item_info: {
                                    item_id: sparkId,
                                    text: source.item_info?.text || creativeVar.caption,
                                    anchor_list: null,
                                    stitch_original_item_id: '0',
                                    auth_code: source.sparkCode!,
                                    duet_original_item_id: '0',
                                    is_multi_duet_stitch: false,
                                    mentioned_item_ids: null
                                },
                                video_info: {
                                    video_id: sparkId,
                                    preview_url: source.video_info?.preview_url || source.mediaUrl,
                                    poster_url: source.video_info?.poster_url || source.thumbnailUrl,
                                    file_name: '',
                                    bit_rate: 0,
                                    size: 0,
                                    width: 0,
                                    height: 0,
                                    signature: '',
                                    duration: 0
                                },
                                user_info: {
                                    identity_display_name: source.user_info?.identity_display_name || 'Creator',
                                    identity_type: '',
                                    identity_id: '',
                                    tiktok_name: source.user_info?.identity_display_name || 'Creator',
                                    profile_image: source.user_info?.identity_info?.avatar_url || ''
                                },
                                auth_info: {
                                    auth_start_time: new Date().toISOString(),
                                    auth_end_time: new Date().toISOString()
                                },
                                platformType: AssetPlatformType.TIKTOK,
                            });
                            break;
                        case 'external_image':
                            state.uploadedAssets.push({
                                id: `external_${index + 1}`,
                                public_id: `external_${index + 1}`,
                                asset_id: `external_${index + 1}`,
                                url: source.mediaUrl,
                                secure_url: source.mediaUrl,
                                thumbnail_url: source.thumbnailUrl,
                                original_filename: `External Image ${index + 1}`,
                                resource_type: 'image', // Always image for external_image type
                                platformType: AssetPlatformType.CLOUDINARY,
                                batchId: '', version: 1, version_id: '', signature: '',
                                width: 0, height: 0, format: '', created_at: new Date().toISOString(), bytes: 0,
                                type: 'upload', etag: '', placeholder: false, access_mode: 'public',
                                path: source.mediaUrl, tags: [], folder: '', existing: true,
                            });
                            break;
                    }
                    if (index === 0) {
                        state.callToAction = creativeVar.cta;
                    }
                });
                if (input.creatives.length === 0 && state.callToAction?.value === '') {
                    state.callToAction = { value: 'LEARN_MORE', label: 'Learn More' };
                }
                // Set a preview asset if any creatives were added
                if (state.uploadedAssets.length > 0) {
                    state.previewedAsset = transformPostsToCreativeFormat([state.uploadedAssets[0]])[0];
                } else if (state.selectedInstagramPosts.length > 0) {
                    state.previewedAsset = transformPostsToCreativeFormat([state.selectedInstagramPosts[0]])[0];
                } else if (state.selectedSparkCodes.length > 0) {
                    state.previewedAsset = transformPostsToCreativeFormat([state.selectedSparkCodes[0]])[0];
                }


                // Targeting - simplified for now, using defaults or broad categories
                // This part would need significant enhancement for full agent_notes parsing
                resetGeographyBuckets(); // Resets to default based on playbookType

                if (agentCampaign.agent_notes?.targeted_locations && agentCampaign.agent_notes.targeted_locations.length > 0) {
                    // Basic example: create one bucket with all locations. This is very naive.
                    // A proper implementation would involve searching/matching these location strings.
                    state.geographyBuckets = [{
                        id: 1, name: "Agent Specified Locations", percentage: 100,
                        geographies: agentCampaign.agent_notes.targeted_locations.map(locStr => ({
                            id: locStr, name: locStr, label: locStr, type: 'country' // Assuming country, highly simplified
                        }))
                    }];
                }

                // Map targeted_audiences from agent_notes to selectedAudienceGroups structure
                if (agentCampaign.agent_notes?.targeted_audiences && agentCampaign.agent_notes.targeted_audiences.length > 0) {
                    console.log('🔄 Setting agent audience groups:', agentCampaign.agent_notes.targeted_audiences);
                    const agentAudienceGroups = agentCampaign.agent_notes.targeted_audiences.map((audienceDescription, index) => ({
                        id: `agent_audience_${index + 1}`,
                        title: audienceDescription,
                        tags: [{
                            id: index + 1, // Use numeric ID
                            title: audienceDescription,
                            audience_size: 0, // Agent doesn't provide size data
                            type: 'interestTargetingAudience' as const
                        }]
                    }));
                    console.log('🔄 Setting agent audience groups:', agentAudienceGroups);
                    // Replace existing audience groups instead of appending to prevent duplication
                    state.selectedAudienceGroups = agentAudienceGroups;
                }

                // state.minAge, state.maxAge, state.genders, state.languages will use defaults from initializeCampaignState unless overridden

                // Conversions
                state.conversionsEventName = input.conversionEventName;
                state.conversionsEnabled = !!input.conversionEventName;

                // Store campaign ID from AgentCampaign
                if (agentCampaign.id) {
                    if (!state.campaign) {
                        state.campaign = {} as CampaignDetails; // Initialize if null
                    }
                    // Assuming CampaignDetails.id is string. If number, it needs parsing.
                    state.campaign.id = parseInt(agentCampaign.id, 10);
                    // Note: CampaignDetails doesn't have a name property
                    // Potentially map other AgentCampaign fields to CampaignDetails if needed
                }

                // // If checkoutDetails are present, try to map them to automationFeeData
                // if (agentCampaign.checkoutDetails) {
                //     const cd = agentCampaign.checkoutDetails;
                //     state.automationFeeData = {
                //         adPlatform: state.adPlatform!,
                //         budget: input.totalAdsBudget || input.budget,
                //         currency: cd.currency || 'USD',
                //         usesExpressSetup: false, // Default assumption, might need adjustment
                //         totalCost: cd.totalCost,
                //         adsBudget: cd.adsBudget,
                //         takeRate: cd.takeRate,
                //         lineItems: cd.lineItems.map((item: any) => ({
                //             type: item.type,
                //             name: item.name,
                //             description: item.description,
                //             amount: item.amount,
                //         })),
                //         paymentProcessorFee: 0, // Default if not present
                //         revenueCatTotal: cd.totalCost, // Assuming totalCost is what RevenueCat would see
                //         errors: [],
                //     };
                // }


                state.campaignInitialized = true;
                state.readyForSubmission = agentCampaign.readyForSubmission === undefined ? true : agentCampaign.readyForSubmission;
            });
        }
    }))
);

export default useCampaignBuilder;
/**
   * Transforms various post types into a standardized CreativeSelectorPost format
   * @param posts Array of posts to transform (Instagram posts, uploaded assets, or spark posts)
   * @returns Array of transformed and filtered CreativeSelectorPost objects
   */

export const transformPostsToCreativeFormat = (
    posts: (InstagramPost | CloudinaryUploadedFileType | TikTokSparkAd | ImportFromURLResponse)[]
): CreativeSelectorPost[] => {
    if (!posts?.length) return [];

    return posts
        .filter((post) => {
            if (post.platformType === AssetPlatformType.INSTAGRAM) {
                return post.media_url || post.thumbnail_url;
            } else if (post.platformType === AssetPlatformType.CLOUDINARY) {
                return post.url;
            } else if (post.platformType === AssetPlatformType.IMPORTED_FROM_URL) {
                return post.assetUrl;
            } else if (post.item_info || post.platformType === AssetPlatformType.TIKTOK) {
                // For TikTok, ensure we have either a valid item_id or video_id
                return post.item_info?.item_id || post.video_info?.video_id;
            }
        })
        .map((post): CreativeSelectorPost | null => {
            switch (post.platformType) {
                case AssetPlatformType.IMPORTED_FROM_URL: {
                    const importedFromUrlPost = post as ImportFromURLResponse;
                    return {
                        id: importedFromUrlPost.id,
                        title: importedFromUrlPost.platformCaption,
                        timestamp: importedFromUrlPost.timestamp,
                        isRecommended: false,
                        originalPostType: AssetPlatformType.IMPORTED_FROM_URL,
                        mediaType: importedFromUrlPost.mediaType,
                        assetUrl: importedFromUrlPost.assetUrl,
                        thumbnailUrl: importedFromUrlPost.thumbnailUrl,
                        platformCaption: importedFromUrlPost.platformCaption,
                    }
                }
                case AssetPlatformType.TIKTOK: {
                    const tiktokPost = post as TikTokSparkAd;
                    // Generate a consistent ID even if item_id is not available
                    const postId = tiktokPost.item_info!.item_id
                    return {
                        id: postId,
                        title: tiktokPost.user_info?.identity_display_name || tiktokPost.item_info?.item_id || '',
                        timestamp: tiktokPost.auth_info?.auth_start_time || '',
                        isRecommended: false,
                        originalPostType: AssetPlatformType.TIKTOK,
                        mediaType: MediaType.VIDEO,
                        assetUrl: tiktokPost.video_info?.preview_url || '',
                        thumbnailUrl: tiktokPost.video_info?.poster_url || '',
                        platformCaption: tiktokPost.item_info?.text || '',
                        description: `TikTok video from ${tiktokPost.user_info?.identity_display_name || 'creator'}`,
                        tags: [],
                        likesCount: tiktokPost.user_info?.identity_info?.likes_count,
                        commentsCount: 0,
                        sharesCount: 0,
                        backgroundColor: '',
                    };
                }

                case AssetPlatformType.INSTAGRAM: {
                    const instagramPost = post as InstagramPost;
                    // Skip posts without any media
                    if (!instagramPost.media_url && !instagramPost.thumbnail_url) return null;

                    // For videos, prefer thumbnail_url, otherwise use media_url
                    const mediaType = instagramPost.media_type === 'VIDEO' ? MediaType.VIDEO :
                        instagramPost.media_type === 'CAROUSEL_ALBUM' ? MediaType.CAROUSEL_ALBUM :
                            MediaType.IMAGE;

                    const assetUrl = instagramPost.media_url || instagramPost.thumbnail_url;

                    let thumbnailUrl = instagramPost.thumbnail_url || assetUrl;
                    // Skip if we still don't have a valid image URL
                    if (!assetUrl) return null;

                    // Handle carousel children if they exist
                    const children = instagramPost.children?.data?.map((child: {
                        id: string;
                        media_type: string;
                        media_url: string;
                    }) => ({
                        id: child.id,
                        mediaType: child.media_type === 'VIDEO' ? MediaType.VIDEO : MediaType.IMAGE,
                        mediaUrl: child.media_url
                    })) || undefined;

                    return {
                        id: instagramPost.id,
                        title: instagramPost.caption || 'Instagram Post',
                        timestamp: instagramPost.timestamp,
                        isRecommended: false,
                        originalPostType: AssetPlatformType.INSTAGRAM,
                        mediaType: mediaType,
                        assetUrl: assetUrl,
                        ig_id: instagramPost.ig_id || undefined,
                        fb_id: instagramPost.fb_id || undefined,
                        thumbnailUrl: thumbnailUrl,
                        platformCaption: instagramPost.caption,
                        likesCount: instagramPost.like_count,
                        commentsCount: instagramPost.comments_count,
                        children: children,
                        currentCarouselIndex: 0,
                        permalink: instagramPost.permalink,
                        availableAdPlacements: instagramPost.availableAdPlacements || []
                    };
                }

                case AssetPlatformType.CLOUDINARY: {
                    // Skip assets without a URL
                    if (!post.url) return null;

                    return {
                        id: post.id,
                        thumbnailUrl: post.thumbnail_url,
                        title: post.original_filename || 'Uploaded Asset',
                        timestamp: post.created_at,
                        isRecommended: false,
                        assetUrl: post.url,
                        mediaType: post.resource_type === 'video' ? MediaType.VIDEO : MediaType.IMAGE,
                        originalPostType: AssetPlatformType.CLOUDINARY,
                    };
                }
            }
        })
        .filter((post): post is CreativeSelectorPost => post !== null);
}
const fakeAsync = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}